import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { useMarketAreas } from "../../contexts/MarketAreaContext";
import { useMap } from "../../contexts/MapContext";
import { useParams } from "react-router-dom";
import { StyleSettingsPanel } from "../market-areas/StyleSettingsPanel";
import ThemeSelector from "../market-areas/ThemeSelector";
// Main ImportDialog component
export default function ImportDialog({ isOpen, onClose, projectId }) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [fileName, setFileName] = useState("");
    const [fileSelected, setFileSelected] = useState(false);
    const fileInputRef = useRef(null);

    // New state for preview modal
    const [showPreview, setShowPreview] = useState(false);
    const [detectedMarketAreas, setDetectedMarketAreas] = useState([]);

    // Get the current project ID from URL params if not provided
    const { projectId: urlProjectId } = useParams();
    const effectiveProjectId = projectId || urlProjectId;

    // Get the full market areas context and map context
    const marketAreasContext = useMarketAreas();
    const mapContext = useMap();
    const {
        addActiveLayer,
        setVisibleMarketAreaIds,
        updateFeatureStyles,
        clearMarketAreaGraphics,
        selectionGraphicsLayer,
        featureLayers,
        mapView,
        drawRadius,
        drawDriveTimePolygon,
        calculateDriveTimePolygon,
        zoomToMarketArea,
        hideAllFeatureLayers
    } = mapContext;

    // Define supported market area types - all types are now supported for import
    const SUPPORTED_MARKET_AREA_TYPES = [
        'zip', 'place', 'county', 'tract', 'blockgroup', 'block',
        'cbsa', 'state', 'radius', 'drivetime', 'custom', 'site_location', 'usa'
    ];

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFileName(file.name);
            setFileSelected(true);
        } else {
            setFileName("");
            setFileSelected(false);
        }
    };

    const resetForm = () => {
        setFileName("");
        setFileSelected(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setShowPreview(false);
        setDetectedMarketAreas([]);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    // Modified to show preview instead of immediate import
    const handleProcessFile = async () => {
        if (!fileSelected || !fileInputRef.current.files[0]) {
            toast.error("Please select a file to import");
            return;
        }

        if (!effectiveProjectId) {
            toast.error("Cannot import: Project ID is missing");
            return;
        }

        setIsProcessing(true);
        const loadingToast = toast.loading("Processing import file...");

        try {
            const file = fileInputRef.current.files[0];
            const { jsonData, workbook, excelJsWorkbook } = await readExcelFile(file);

            if (!jsonData || !jsonData.length) {
                toast.error("No valid data found in the Excel file");
                toast.dismiss(loadingToast);
                setIsProcessing(false);
                return;
            }

            console.log("Excel data loaded with", jsonData.length, "rows");

            // Try to detect if this is a standard format or the special template format
            const isTemplateFormat = detectTemplateFormat(jsonData);
            console.log("Detected format:", isTemplateFormat ? "template" : "standard");

            let allMarketAreas = [];
            if (isTemplateFormat) {
                allMarketAreas = processTemplateData(jsonData, workbook, excelJsWorkbook);
            } else {
                allMarketAreas = processStandardData(jsonData, workbook, excelJsWorkbook);
            }

            // Filter to only include supported types
            const marketAreas = allMarketAreas.filter(ma => SUPPORTED_MARKET_AREA_TYPES.includes(ma.ma_type));
            
            // Log how many areas were filtered out
            const filteredOutCount = allMarketAreas.length - marketAreas.length;
            if (filteredOutCount > 0) {
                console.log(`Filtered out ${filteredOutCount} unsupported market area types.`);
            }

            if (marketAreas.length === 0) {
                toast.error("No supported market areas found in the file.");
                toast.dismiss(loadingToast);
                setIsProcessing(false);
                return;
            }

            // Set detected market areas and show preview
            setDetectedMarketAreas(marketAreas);
            setShowPreview(true);
            toast.dismiss(loadingToast);
            if (filteredOutCount > 0) {
                toast.success(`Found ${marketAreas.length} supported market areas. Ignored ${filteredOutCount} unsupported types.`);
            } else {
                toast.success(`Found ${marketAreas.length} market areas. Please review before importing.`);
            }
        } catch (error) {
            console.error("Import processing failed:", error);
            toast.dismiss(loadingToast);
            toast.error(`Import processing failed: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };


    // State mappings to help with filtering
    const STATE_MAPPINGS = {
        // Full state name to FIPS code
        nameToFips: {
            'Alabama': '01', 'Alaska': '02', 'Arizona': '04', 'Arkansas': '05',
            'California': '06', 'Colorado': '08', 'Connecticut': '09', 'Delaware': '10',
            'Florida': '12', 'Georgia': '13', 'Hawaii': '15', 'Idaho': '16',
            'Illinois': '17', 'Indiana': '18', 'Iowa': '19', 'Kansas': '20',
            'Kentucky': '21', 'Louisiana': '22', 'Maine': '23', 'Maryland': '24',
            'Massachusetts': '25', 'Michigan': '26', 'Minnesota': '27', 'Mississippi': '28',
            'Missouri': '29', 'Montana': '30', 'Nebraska': '31', 'Nevada': '32',
            'New Hampshire': '33', 'New Jersey': '34', 'New Mexico': '35', 'New York': '36',
            'North Carolina': '37', 'North Dakota': '38', 'Ohio': '39', 'Oklahoma': '40',
            'Oregon': '41', 'Pennsylvania': '42', 'Rhode Island': '44', 'South Carolina': '45',
            'South Dakota': '46', 'Tennessee': '47', 'Texas': '48', 'Utah': '49',
            'Vermont': '50', 'Virginia': '51', 'Washington': '53', 'West Virginia': '54',
            'Wisconsin': '55', 'Wyoming': '56', 'District of Columbia': '11'
        },

        // State abbreviation to FIPS code
        abbrToFips: {
            'AL': '01', 'AK': '02', 'AZ': '04', 'AR': '05', 'CA': '06', 'CO': '08', 'CT': '09',
            'DE': '10', 'DC': '11', 'FL': '12', 'GA': '13', 'HI': '15', 'ID': '16', 'IL': '17',
            'IN': '18', 'IA': '19', 'KS': '20', 'KY': '21', 'LA': '22', 'ME': '23', 'MD': '24',
            'MA': '25', 'MI': '26', 'MN': '27', 'MS': '28', 'MO': '29', 'MT': '30', 'NE': '31',
            'NV': '32', 'NH': '33', 'NJ': '34', 'NM': '35', 'NY': '36', 'NC': '37', 'ND': '38',
            'OH': '39', 'OK': '40', 'OR': '41', 'PA': '42', 'RI': '44', 'SC': '45', 'SD': '46',
            'TN': '47', 'TX': '48', 'UT': '49', 'VT': '50', 'VA': '51', 'WA': '53', 'WV': '54',
            'WI': '55', 'WY': '56'
        },

        // Helper function to get FIPS code from state name or abbreviation
        getStateFips: function (state) {
            if (!state) return null;

            // Normalize state input
            const normalizedState = state.trim();

            // Check if it's already a FIPS code (numeric)
            if (/^\d{1,2}$/.test(normalizedState)) {
                return normalizedState.padStart(2, '0');
            }

            // Check if it's a 2-letter abbreviation
            if (normalizedState.length === 2) {
                return this.abbrToFips[normalizedState.toUpperCase()] || null;
            }

            // Check if it's a full state name
            return this.nameToFips[normalizedState] || null;
        },

        // Helper function to get state abbreviation from state name, FIPS, or abbreviation
        getStateAbbr: function (state) {
            if (!state) return null;

            const normalizedState = state.trim();

            // Check if it's already a 2-letter abbreviation
            if (normalizedState.length === 2 && /^[A-Za-z]{2}$/.test(normalizedState)) {
                return normalizedState.toUpperCase();
            }

            // Check if it's a FIPS code, convert to abbreviation
            if (/^\d{1,2}$/.test(normalizedState)) {
                const fips = normalizedState.padStart(2, '0');
                for (const [abbr, code] of Object.entries(this.abbrToFips)) {
                    if (code === fips) return abbr;
                }
                return null;
            }

            // Check if it's a full state name, find the abbreviation
            for (const [name, fips] of Object.entries(this.nameToFips)) {
                if (name.toLowerCase() === normalizedState.toLowerCase()) {
                    // Find the abbreviation for this FIPS code
                    for (const [abbr, code] of Object.entries(this.abbrToFips)) {
                        if (code === fips) return abbr;
                    }
                }
            }

            return null;
        }
    };

    // Direct REST API URLs for each layer type (bypasses layer initialization issues)
    const LAYER_URLS = {
        zip: 'https://services2.arcgis.com/FiaPA4ga0iQKduv3/arcgis/rest/services/Census_ZIP_Code_Tabulation_Areas_2010_v1/FeatureServer/0',
        county: 'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_ACS2024/MapServer/82',
        place: [
            'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_ACS2024/MapServer/28',
            'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_ACS2024/MapServer/30'
        ],
        tract: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_Census_Tracts/FeatureServer/0',
        block: 'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/Tracts_Blocks/MapServer/2',
        blockgroup: 'https://services.arcgis.com/P3ePLMYs2RVChkJx/ArcGIS/rest/services/USA_Census_BlockGroups/FeatureServer/0',
        state: 'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_ACS2024/MapServer/80',
        cbsa: [
            'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_ACS2024/MapServer/91',
            'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/tigerWMS_ACS2024/MapServer/93'
        ]
    };

    // Query ArcGIS REST API directly (bypasses destroyed layer issues)
    const queryRestApi = async (url, whereClause) => {
        try {
            const params = new URLSearchParams({
                where: whereClause,
                outFields: '*',
                returnGeometry: 'true',
                f: 'json',
                outSR: '102100'  // Return in Web Mercator to match map's spatial reference
            });

            console.log(`[REST API] Querying ${url} with where: ${whereClause}`);
            const response = await fetch(`${url}/query?${params}`);

            if (!response.ok) {
                console.warn(`[REST API] Request failed with status ${response.status}`);
                return [];
            }

            const data = await response.json();

            if (data.error) {
                console.warn(`[REST API] Query error:`, data.error);
                return [];
            }

            console.log(`[REST API] Found ${data.features?.length || 0} features`);
            return data.features || [];
        } catch (error) {
            console.error(`[REST API] Error querying ${url}:`, error);
            return [];
        }
    };

    // Build where clause for each market area type
    const buildWhereClause = (marketArea) => {
        const { ma_type, locations } = marketArea;

        if (ma_type === 'zip') {
            const validZips = locations
                .map(loc => {
                    const zipValue = (loc.id || loc.name || "").trim();
                    if (!zipValue) return null;
                    const formattedZip = zipValue.padStart(5, '0');
                    return `ZCTA5 = '${formattedZip}'`;
                })
                .filter(Boolean);
            return validZips.length > 0 ? validZips.join(" OR ") : null;
        }

        if (ma_type === 'county') {
            return locations.map(loc => {
                const countyName = (loc.id || loc.name || "")
                    .replace(/\s+County$/i, "")
                    .trim()
                    .replace(/'/g, "''");
                const stateValue = loc.state || "CA";
                const stateFips = STATE_MAPPINGS.getStateFips(stateValue);
                const stateFilter = stateFips ? ` AND STATE = '${stateFips}'` : "";
                return `(UPPER(NAME) LIKE UPPER('%${countyName}%')${stateFilter})`;
            }).join(" OR ");
        }

        if (ma_type === 'place') {
            return locations.map(loc => {
                const placeName = (loc.id || loc.name || "")
                    .trim()
                    .replace(/\s+(city|town|village|borough|cdp)$/i, "")
                    .replace(/'/g, "''");
                const stateValue = loc.state || "CA";
                const stateFips = STATE_MAPPINGS.getStateFips(stateValue);
                const stateFilter = stateFips ? ` AND STATE = '${stateFips}'` : "";
                return `(UPPER(NAME) LIKE UPPER('%${placeName}%')${stateFilter})`;
            }).join(" OR ");
        }

        if (ma_type === 'tract') {
            const validTracts = locations.map(loc => {
                const tractValue = (loc.id || loc.name || "").trim();
                if (!tractValue) return null;
                const stateAbbr = STATE_MAPPINGS.getStateAbbr(loc.state || "CA");
                if (/^\d{11}$/.test(tractValue)) {
                    return `FIPS = '${tractValue}'`;
                }
                let query = `FIPS LIKE '%${tractValue}'`;
                if (stateAbbr) {
                    query = `(${query} AND STATE_ABBR = '${stateAbbr}')`;
                }
                return query;
            }).filter(Boolean);
            return validTracts.length > 0 ? validTracts.join(" OR ") : null;
        }

        if (ma_type === 'blockgroup') {
            const validBGs = locations.map(loc => {
                const bgValue = (loc.id || loc.name || "").trim();
                if (!bgValue) return null;
                const stateAbbr = STATE_MAPPINGS.getStateAbbr(loc.state || "CA");
                if (/^\d{12}$/.test(bgValue)) {
                    return `FIPS = '${bgValue}'`;
                }
                let query = `FIPS LIKE '%${bgValue}'`;
                if (stateAbbr) {
                    query = `(${query} AND STATE_ABBR = '${stateAbbr}')`;
                }
                return query;
            }).filter(Boolean);
            return validBGs.length > 0 ? validBGs.join(" OR ") : null;
        }

        if (ma_type === 'block') {
            const validBlocks = locations.map(loc => {
                const blockValue = (loc.id || loc.name || "").trim();
                if (!blockValue) return null;
                const stateFips = STATE_MAPPINGS.getStateFips(loc.state || "CA");
                if (/^\d{15}$/.test(blockValue)) {
                    return `GEOID = '${blockValue}'`;
                }
                let query = `GEOID LIKE '%${blockValue}'`;
                if (stateFips) {
                    query = `(${query} AND STATE = '${stateFips}')`;
                }
                return query;
            }).filter(Boolean);
            return validBlocks.length > 0 ? validBlocks.join(" OR ") : null;
        }

        if (ma_type === 'cbsa') {
            return locations.map(loc => {
                const cbsaValue = (loc.id || loc.name || "").trim().replace(/'/g, "''");
                if (!cbsaValue) return null;
                if (/^\d+$/.test(cbsaValue)) {
                    return `GEOID = '${cbsaValue}'`;
                }
                return `UPPER(NAME) LIKE UPPER('%${cbsaValue}%')`;
            }).filter(Boolean).join(" OR ");
        }

        if (ma_type === 'state') {
            return locations.map(loc => {
                const stateValue = (loc.id || loc.name || "").trim();
                if (!stateValue) return null;
                const stateFips = STATE_MAPPINGS.getStateFips(stateValue);
                if (stateFips) {
                    return `GEOID = '${stateFips}'`;
                }
                return `UPPER(NAME) LIKE UPPER('%${stateValue.replace(/'/g, "''")}%')`;
            }).filter(Boolean).join(" OR ");
        }

        return null;
    };

    // Query features using direct REST API (main function)
    const queryFeaturesDirectly = async (marketArea) => {
        const { ma_type } = marketArea;
        console.log(`[REST API] queryFeaturesDirectly called for type: ${ma_type}`);

        // Skip types that don't use feature layers
        if (['radius', 'drivetime', 'custom', 'site_location', 'usa'].includes(ma_type)) {
            console.log(`[REST API] Skipping ${ma_type} - doesn't use feature layers`);
            return [];
        }

        const layerUrl = LAYER_URLS[ma_type];
        if (!layerUrl) {
            console.warn(`[REST API] No URL configured for type ${ma_type}`);
            return [];
        }
        console.log(`[REST API] Using URL for ${ma_type}:`, layerUrl);

        const whereClause = buildWhereClause(marketArea);
        console.log(`[REST API] Built where clause for ${ma_type}:`, whereClause);
        if (!whereClause) {
            console.warn(`[REST API] Empty where clause for ${marketArea.id}`);
            return [];
        }

        // Handle multiple URLs (place, cbsa)
        if (Array.isArray(layerUrl)) {
            console.log(`[REST API] Trying ${layerUrl.length} URLs for ${ma_type}`);
            for (const url of layerUrl) {
                const features = await queryRestApi(url, whereClause);
                if (features.length > 0) {
                    console.log(`[REST API] Found ${features.length} features from ${url}`);
                    return features;
                }
            }
            console.warn(`[REST API] No features found from any URL for ${ma_type}`);
            return [];
        }

        const features = await queryRestApi(layerUrl, whereClause);
        console.log(`[REST API] Query complete for ${ma_type}, found ${features.length} features`);
        return features;
    };

    const queryFeaturesForMarketArea = async (marketArea) => {
        console.log("Querying features for market area:", marketArea.id);

        if (!marketArea || !marketArea.locations || marketArea.locations.length === 0) {
            console.warn(`Market area ${marketArea.id} has no locations to query`);
            return [];
        }

        // Use direct REST API queries as the primary method (bypasses destroyed layer issues)
        console.log(`[Import] Using direct REST API for ${marketArea.ma_type}`);
        const directFeatures = await queryFeaturesDirectly(marketArea);
        if (directFeatures.length > 0) {
            console.log(`[Import] Direct REST API found ${directFeatures.length} features for ${marketArea.name}`);
            return directFeatures;
        }

        // Fallback to layer-based queries if REST API fails and layers are available
        if (!featureLayers || !mapView) {
            console.warn("Feature layers or map view not initialized, and REST API returned no results");
            return [];
        }

        try {
            const { default: Query } = await import("@arcgis/core/rest/support/Query");

            let layer = featureLayers[marketArea.ma_type];

            // Check if layer is destroyed or not available
            if (!layer || layer.destroyed) {
                console.warn(`Layer for ${marketArea.ma_type} is destroyed or missing, REST API also failed`);
                return [];
            }
    
            // Build a where clause based on the market area type and locations
            let whereClause = "";
    
            if (marketArea.ma_type === 'zip') {
                // For ZIP codes, use ZCTA5 field (ZIP layer doesn't have STATE field)
                const validZips = marketArea.locations
                    .map(loc => {
                        const zipValue = (loc.id || loc.name || "").trim();
                        if (!zipValue) return null;

                        // Format ZIP code (pad to 5 digits) and query ZCTA5 field
                        const formattedZip = zipValue.padStart(5, '0');
                        return `ZCTA5 = '${formattedZip}'`;
                    })
                    .filter(Boolean); // Remove null/empty values

                if (validZips.length === 0) {
                    console.warn("No valid ZIP codes found for market area:", marketArea.id);
                    return [];
                }

                whereClause = validZips.join(" OR ");
                console.log("ZIP where clause:", whereClause);
            }
            else if (marketArea.ma_type === 'county') {
                // For counties, use the NAME field with LIKE operator and add state filtering
                whereClause = marketArea.locations.map(loc => {
                    const countyName = (loc.id || loc.name || "Unknown County")
                        .replace(/\s+County$/i, "") // Remove "County" suffix if present
                        .trim()
                        .replace(/'/g, "''"); // Escape single quotes
    
                    // Get state information for filtering
                    let stateFilter = "";
                    const stateValue = loc.state || "CA";
    
                    // Convert to FIPS code if needed
                    let stateFips = STATE_MAPPINGS.getStateFips(stateValue);
                    if (stateFips) {
                        stateFilter = ` AND STATE = '${stateFips}'`;
                    }
    
                    console.log(`County filter: ${countyName} in state: ${stateValue} (FIPS: ${stateFips})`);
                    return `(UPPER(NAME) LIKE UPPER('%${countyName}%')${stateFilter})`;
                }).join(" OR ");
    
                console.log("County where clause with state filtering:", whereClause);
            }
            else if (marketArea.ma_type === 'place') {
                // For places, use the NAME field with LIKE operator and add state filtering
                whereClause = marketArea.locations.map(loc => {
                    const placeName = (loc.id || loc.name || "Unknown Place")
                        .trim()
                        .replace(/\s+(city|town|village|borough|cdp)$/i, "") // Remove type suffix if present
                        .replace(/'/g, "''"); // Escape single quotes

                    // Get state information for filtering
                    let stateFilter = "";
                    const stateValue = loc.state || "CA";

                    // Convert to FIPS code if needed
                    let stateFips = STATE_MAPPINGS.getStateFips(stateValue);
                    if (stateFips) {
                        stateFilter = ` AND STATE = '${stateFips}'`;
                    }

                    console.log(`Place filter: "${placeName}" in state: ${stateValue} (FIPS: ${stateFips})`);
                    return `(UPPER(NAME) LIKE UPPER('%${placeName}%')${stateFilter})`;
                }).join(" OR ");

                console.log("Place where clause with state filtering:", whereClause);
            }
            else if (marketArea.ma_type === 'tract') {
                // For census tracts, use FIPS field (11-digit state+county+tract code)
                // Layer fields: FIPS, STATE_ABBR, STATE_FIPS, COUNTY_FIPS, TRACT_FIPS
                const validTracts = marketArea.locations
                    .map(loc => {
                        const tractValue = (loc.id || loc.name || "").trim();
                        if (!tractValue) return null;

                        // Get state information for filtering
                        const stateValue = loc.state || "CA";
                        const stateAbbr = STATE_MAPPINGS.getStateAbbr(stateValue);
                        const stateFips = STATE_MAPPINGS.getStateFips(stateValue);

                        // If it's a full FIPS code (11 digits), query directly
                        if (/^\d{11}$/.test(tractValue)) {
                            return `FIPS = '${tractValue}'`;
                        }

                        // Build query that matches on FIPS ending or TRACT_FIPS
                        // with state filtering using STATE_ABBR
                        let query = `FIPS LIKE '%${tractValue}'`;
                        if (stateAbbr) {
                            query = `(${query} AND STATE_ABBR = '${stateAbbr}')`;
                        }
                        return query;
                    })
                    .filter(Boolean);

                if (validTracts.length === 0) {
                    console.warn("No valid tract codes found for market area:", marketArea.id);
                    return [];
                }

                whereClause = validTracts.join(" OR ");
                console.log("Tract where clause:", whereClause);
            }
            else if (marketArea.ma_type === 'blockgroup') {
                // For census block groups, use FIPS field (12-digit state+county+tract+blockgroup code)
                // Layer fields: FIPS, STATE_ABBR, COUNTY_FIPS, TRACT_FIPS, BLOCKGROUP_FIPS
                const validBlockGroups = marketArea.locations
                    .map(loc => {
                        const bgValue = (loc.id || loc.name || "").trim();
                        if (!bgValue) return null;

                        const stateValue = loc.state || "CA";
                        const stateAbbr = STATE_MAPPINGS.getStateAbbr(stateValue);

                        // If it's a full FIPS code (12 digits), query directly
                        if (/^\d{12}$/.test(bgValue)) {
                            return `FIPS = '${bgValue}'`;
                        }

                        // Build query that matches on FIPS ending
                        // with state filtering using STATE_ABBR
                        let query = `FIPS LIKE '%${bgValue}'`;
                        if (stateAbbr) {
                            query = `(${query} AND STATE_ABBR = '${stateAbbr}')`;
                        }
                        return query;
                    })
                    .filter(Boolean);

                if (validBlockGroups.length === 0) {
                    console.warn("No valid block group codes found for market area:", marketArea.id);
                    return [];
                }

                whereClause = validBlockGroups.join(" OR ");
                console.log("Block Group where clause:", whereClause);
            }
            else if (marketArea.ma_type === 'block') {
                // For census blocks, use GEOID field (15-digit code) or BLOCK field
                // Layer fields: OID, GEOID, STATE, COUNTY, TRACT, BLOCK
                const validBlocks = marketArea.locations
                    .map(loc => {
                        const blockValue = (loc.id || loc.name || "").trim();
                        if (!blockValue) return null;

                        const stateValue = loc.state || "CA";
                        const stateFips = STATE_MAPPINGS.getStateFips(stateValue);

                        // If it's a full GEOID (15 digits), query directly
                        if (/^\d{15}$/.test(blockValue)) {
                            return `GEOID = '${blockValue}'`;
                        }

                        // Build query that matches on GEOID ending
                        // with state filtering using STATE field
                        let query = `GEOID LIKE '%${blockValue}'`;
                        if (stateFips) {
                            query = `(${query} AND STATE = '${stateFips}')`;
                        }
                        return query;
                    })
                    .filter(Boolean);

                if (validBlocks.length === 0) {
                    console.warn("No valid block codes found for market area:", marketArea.id);
                    return [];
                }

                whereClause = validBlocks.join(" OR ");
                console.log("Block where clause:", whereClause);
            }
            else if (marketArea.ma_type === 'cbsa') {
                // For CBSAs (metropolitan areas), use GEOID or NAME field
                whereClause = marketArea.locations.map(loc => {
                    const cbsaValue = (loc.id || loc.name || "")
                        .trim()
                        .replace(/'/g, "''");

                    if (!cbsaValue) return null;

                    // CBSAs can be matched by GEOID (code) or NAME
                    if (/^\d+$/.test(cbsaValue)) {
                        return `GEOID = '${cbsaValue}'`;
                    } else {
                        return `UPPER(NAME) LIKE UPPER('%${cbsaValue}%')`;
                    }
                }).filter(Boolean).join(" OR ");

                console.log("CBSA where clause:", whereClause);
            }
            else if (marketArea.ma_type === 'state') {
                // For states, use STATE field (FIPS code) or NAME
                whereClause = marketArea.locations.map(loc => {
                    const stateValue = (loc.id || loc.name || "").trim();
                    if (!stateValue) return null;

                    // Try to get FIPS code
                    let stateFips = STATE_MAPPINGS.getStateFips(stateValue);
                    if (stateFips) {
                        return `STATE = '${stateFips}'`;
                    } else {
                        // Fall back to name matching
                        return `UPPER(NAME) LIKE UPPER('%${stateValue.replace(/'/g, "''")}%')`;
                    }
                }).filter(Boolean).join(" OR ");

                console.log("State where clause:", whereClause);
            }
            else if (marketArea.ma_type === 'radius' || marketArea.ma_type === 'drivetime' ||
                     marketArea.ma_type === 'custom' || marketArea.ma_type === 'site_location' ||
                     marketArea.ma_type === 'usa') {
                // These types don't use feature layer queries - they have their own geometry handling
                console.log(`Market area type ${marketArea.ma_type} doesn't require feature layer queries`);
                return [];
            }
            else {
                // Fallback for any other types
                console.warn(`Unknown market area type: ${marketArea.ma_type}`);
                return [];
            }
    
            // Skip query if where clause is empty
            if (!whereClause) {
                console.warn(`Empty where clause for market area ${marketArea.id}`);
                return [];
            }
    
            console.log(`Querying ${marketArea.ma_type} layer with where clause:`, whereClause);
    
            // Create query with added timeout and error handling
            const query = new Query({
                where: whereClause,
                outFields: ["*"],
                returnGeometry: true,
                outSpatialReference: mapView.spatialReference,
                maxRecordCount: 100, // Limit results
                num: 100, // Limit results
                start: 0
                // Removed returnDistinctValues: true to fix the conflict with returnGeometry
            });
    
            // Query features with error handling
            let features = [];

            // For group layers (layer.featureLayers), try each sublayer
            if (layer.featureLayers && Array.isArray(layer.featureLayers)) {
                for (const sublayer of layer.featureLayers) {
                    // Skip destroyed or invalid sublayers
                    if (!sublayer || sublayer.destroyed || !sublayer.queryFeatures) continue;

                    try {
                        const result = await sublayer.queryFeatures(query);
                        if (result && result.features && result.features.length > 0) {
                            features = result.features;
                            console.log(`Found ${features.length} features in sublayer for ${marketArea.ma_type}`);
                            break; // Found features, stop checking other sublayers
                        }
                    } catch (error) {
                        // Check if layer was destroyed during query
                        if (error.name === 'load:instance-destroyed') {
                            console.warn(`Sublayer for ${marketArea.ma_type} was destroyed during query, skipping...`);
                            continue;
                        }
                        console.warn(`Error querying sublayer for ${marketArea.ma_type}:`, error);
                        // If specific error about DISTINCT and Geometry, try without distinct
                        if (error.message && error.message.includes('Geometry is not supported with DISTINCT')) {
                            console.log('Retrying query without DISTINCT parameter');
                            try {
                                const retryResult = await sublayer.queryFeatures(query);
                                if (retryResult && retryResult.features && retryResult.features.length > 0) {
                                    features = retryResult.features;
                                    console.log(`Retry successful! Found ${features.length} features`);
                                    break;
                                }
                            } catch (retryError) {
                                console.warn('Retry also failed:', retryError);
                            }
                        }
                        // Continue to next sublayer
                    }
                }
            }
            // For single layers
            else if (layer.queryFeatures && !layer.destroyed) {
                try {
                    const result = await layer.queryFeatures(query);
                    if (result && result.features) {
                        features = result.features;
                        console.log(`Found ${features.length} features in layer for ${marketArea.ma_type}`);
                    }
                } catch (error) {
                    console.warn(`Error querying features for ${marketArea.ma_type}:`, error);

                    // If layer was destroyed, skip retry attempts
                    if (error.name === 'load:instance-destroyed' || layer.destroyed) {
                        console.warn(`Layer for ${marketArea.ma_type} was destroyed, cannot query`);
                        // Don't try to retry if layer is destroyed
                    }
                    // Add specific handling for place layer errors
                    else if (marketArea.ma_type === 'place' && !layer.destroyed) {
                        console.log("Trying alternative approach for place query...");
                        
                        try {
                            // Try with a simpler query - just match on the beginning of the name
                            const simplifiedQuery = new Query({
                                where: marketArea.locations.map(loc => {
                                    const cleanName = (loc.id || loc.name || "")
                                        .split(/\s+/)[0]  // Take just first word of place name
                                        .replace(/'/g, "''");
                                    if (!cleanName) return '';
                                    
                                    const stateValue = loc.state || "CA";
                                    const stateFips = STATE_MAPPINGS.getStateFips(stateValue);
                                    const stateFilter = stateFips ? ` AND STATE = '${stateFips}'` : "";
                                    
                                    return `(UPPER(NAME) LIKE UPPER('${cleanName}%')${stateFilter})`;
                                })
                                .filter(Boolean) // Remove empty clauses
                                .join(" OR "),
                                outFields: ["*"],
                                returnGeometry: true,
                                outSpatialReference: mapView.spatialReference
                            });
                            
                            // Skip if where clause is empty
                            if (!simplifiedQuery.where) {
                                console.warn("Simplified place query has empty where clause");
                                return features;
                            }
                            
                            console.log("Simplified place query:", simplifiedQuery.where);
                            const retryResult = await layer.queryFeatures(simplifiedQuery);
                            
                            if (retryResult && retryResult.features && retryResult.features.length > 0) {
                                features = retryResult.features;
                                console.log(`Simplified query successful! Found ${features.length} place features`);
                            }
                        } catch (retryError) {
                            console.warn("Alternative place query approach also failed:", retryError);
                        }
                    }
                    
                    // Similar fallback for ZIP code queries
                    if (marketArea.ma_type === 'zip' && features.length === 0 && !layer.destroyed) {
                        console.log("Trying alternative approach for ZIP query...");

                        try {
                            // Try with a more flexible ZIP match using ZCTA5 field
                            const simplifiedQuery = new Query({
                                where: marketArea.locations.map(loc => {
                                    const zipValue = (loc.id || loc.name || "").trim();
                                    if (!zipValue) return '';

                                    // Match on ZCTA5 prefix as last resort
                                    return `ZCTA5 LIKE '${zipValue}%'`;
                                })
                                .filter(Boolean) // Remove empty clauses
                                .join(" OR "),
                                outFields: ["*"],
                                returnGeometry: true,
                                outSpatialReference: mapView.spatialReference
                            });

                            if (!simplifiedQuery.where) {
                                console.warn("Simplified ZIP query has empty where clause");
                                return features;
                            }

                            console.log("Simplified ZIP query:", simplifiedQuery.where);
                            const retryResult = await layer.queryFeatures(simplifiedQuery);

                            if (retryResult && retryResult.features && retryResult.features.length > 0) {
                                features = retryResult.features;
                                console.log(`Simplified ZIP query successful! Found ${features.length} features`);
                            }
                        } catch (retryError) {
                            console.warn("Alternative ZIP query approach also failed:", retryError);
                        }
                    }
                    
                    // Existing error handling for DISTINCT error
                    if (error.message && error.message.includes('Geometry is not supported with DISTINCT')) {
                        console.log('Retrying query without DISTINCT parameter');
                        try {
                            const retryResult = await layer.queryFeatures(query);
                            if (retryResult && retryResult.features) {
                                features = retryResult.features;
                                console.log(`Retry successful! Found ${features.length} features`);
                            }
                        } catch (retryError) {
                            console.warn('Retry also failed:', retryError);
                        }
                    }
                }
            }
    
            console.log(`Query result for ${marketArea.ma_type}:`, {
                hasFeatures: features.length > 0,
                count: features.length
            });
    
            return features;
        } catch (error) {
            console.error(`Error querying features for market area ${marketArea.id}:`, error);
            return [];
        }
    };

    // Helper function to navigate back to map view after import
    const navigateToMapView = () => {
        // Option 1: If you have a button for this, trigger its click
        const mapViewButton = document.getElementById("mapViewButton");
        if (mapViewButton) {
            mapViewButton.click();
            return;
        }

        // Option 2: If you have a function for this
        if (typeof onNavigateToMap === 'function') {
            onNavigateToMap();
            return;
        }

        // Option 3: If you're using React Router
        // navigate('/projects/' + effectiveProjectId);
    };

    // Helper to extract and normalize data for different market area types
    const normalizeMarketAreaData = (marketArea) => {
        // Verify project ID is available
        if (!effectiveProjectId) {
            console.error("No effective project ID found!");
        }
    
        // Common settings for all types
        const normalizedData = {
            ma_type: marketArea.ma_type,
            name: marketArea.name,
            short_name: marketArea.short_name || marketArea.name.substring(0, 20),
            style_settings: {
                fillColor: marketArea.style_settings?.fillColor || "#0078D4",
                fillOpacity: marketArea.style_settings?.noFill ? 0 :
                    (marketArea.style_settings?.fillOpacity || 0.35),
                borderColor: marketArea.style_settings?.borderColor || "#0078D4",
                borderWidth: marketArea.style_settings?.noBorder ? 0 :
                    (marketArea.style_settings?.borderWidth || 2),
                noFill: marketArea.style_settings?.noFill || false,
                noBorder: marketArea.style_settings?.noBorder || false
            },
            project: effectiveProjectId,
            project_id: effectiveProjectId,
            description: `Imported from ${fileName}`
        };
    
        // We only care about zip, place, and county types now
        normalizedData.locations = [];
        
        // Process locations
        if (marketArea.locations && Array.isArray(marketArea.locations)) {
            // When normalizing locations, make sure we have proper state information for each
            normalizedData.locations = marketArea.locations.map(loc => {
                // ALWAYS ensure state info exists for ALL location types
                const state = loc.state || "CA"; // Default to CA if no state
                console.log(`Normalizing location with state: ${state} for ${marketArea.ma_type}`);
    
                // Create proper spatial reference for any existing geometry
                let geometry = loc.geometry;
                if (geometry && !geometry.spatialReference) {
                    geometry = {
                        ...geometry,
                        spatialReference: { wkid: 4326 } // Add WGS84 spatial reference
                    };
                }
    
                // For ZIP codes, ensure proper formatting
                let id = loc.id || loc.name;
                let name = loc.name || loc.id;
                
                if (marketArea.ma_type === 'zip' && id) {
                    // Format ZIP code properly (ensure 5 digits for numeric values)
                    if (/^\d+$/.test(id)) {
                        id = id.padStart(5, '0');
                        name = id; // Sync name with formatted ID
                    }
                }
    
                return {
                    id: id,
                    name: name,
                    state: state, // Always include state
                    geometry: geometry
                };
            });
        }
    
        // Log the normalized locations for debugging
        console.log(`Normalized ${normalizedData.locations.length} locations for ${marketArea.name}:`, 
            normalizedData.locations.map(loc => `${loc.name || loc.id} (${loc.state})`));
        
        return normalizedData;
    };

    // Store existing graphics we want to keep
    const storeExistingGraphics = () => {
        if (!selectionGraphicsLayer) return [];

        return selectionGraphicsLayer.graphics
            .filter(g => g.attributes?.marketAreaId)
            .toArray()
            .map(g => ({
                geometry: g.geometry,
                attributes: g.attributes,
                symbol: g.symbol
            }));
    };

    // Restore existing graphics to the map
    const restoreExistingGraphics = async (existingGraphics) => {
        if (!selectionGraphicsLayer || existingGraphics.length === 0) return;

        try {
            const { default: Graphic } = await import("@arcgis/core/Graphic");
            existingGraphics.forEach(g => {
                const graphic = new Graphic({
                    geometry: g.geometry,
                    attributes: g.attributes,
                    symbol: g.symbol
                });
                selectionGraphicsLayer.add(graphic);
            });
        } catch (error) {
            console.error("Error restoring existing graphics:", error);
        }
    };

    // Update visibility state for market areas
    const updateVisibilityState = (marketAreaIds) => {
        if (!marketAreaIds || marketAreaIds.length === 0) return;

        try {
            // Get current visible market area IDs from localStorage
            const storedVisibleIds = localStorage.getItem(
                `marketAreas.${effectiveProjectId}.visible`
            );
            let currentVisibleIds = storedVisibleIds
                ? JSON.parse(storedVisibleIds)
                : [];

            // Add newly created market area IDs to visible IDs list
            const updatedVisibleIds = [
                ...currentVisibleIds,
                ...marketAreaIds.filter(id => !currentVisibleIds.includes(id))
            ];

            // Update localStorage
            localStorage.setItem(
                `marketAreas.${effectiveProjectId}.visible`,
                JSON.stringify(updatedVisibleIds)
            );

            // Update visible market areas in context
            setVisibleMarketAreaIds(updatedVisibleIds);
        } catch (error) {
            console.error("Error updating visibility state:", error);
        }
    };

    // Import all market areas and track results
    const importAllMarketAreas = async (marketAreas) => {
        const results = {
            importedCount: 0,
            createdMarketAreaIds: [],
            errors: []
        };

        for (const marketArea of marketAreas) {
            try {
                // Skip if missing essential data
                if (!marketArea.name) {
                    results.errors.push(`Unnamed market area: Missing name`);
                    continue;
                }

                // Skip if not a supported type
                if (!SUPPORTED_MARKET_AREA_TYPES.includes(marketArea.ma_type)) {
                    results.errors.push(`${marketArea.name}: Unsupported market area type ${marketArea.ma_type}`);
                    continue;
                }

                // Normalize the market area data based on type
                const normalizedMarketArea = normalizeMarketAreaData(marketArea);
                console.log(`Normalized market area ${marketArea.name} for import:`, normalizedMarketArea);

                // For feature-based market areas, fetch geometry first
                if (normalizedMarketArea.locations?.length > 0) {
                    // Initialize temporary market area with ID to use in queryFeatures
                    const tempMarketArea = {
                        ...normalizedMarketArea,
                        id: `temp-${Date.now()}`
                    };

                    // Query for geometries using direct REST API (no layer initialization needed)
                    try {
                        console.log(`[Import] Querying features for ${normalizedMarketArea.name} (${normalizedMarketArea.ma_type}) with ${tempMarketArea.locations.length} locations`);
                        const features = await queryFeaturesForMarketArea(tempMarketArea);

                        if (features && features.length > 0) {
                            console.log(`[Import] Found ${features.length} features for ${normalizedMarketArea.name}`);
                            console.log(`[Import] Sample feature:`, features[0]?.attributes, 'hasGeometry:', !!features[0]?.geometry);

                            // Match features to locations and add geometries
                            let matchedCount = 0;
                            for (let i = 0; i < normalizedMarketArea.locations.length; i++) {
                                const loc = normalizedMarketArea.locations[i];
                                console.log(`[Import] Matching location ${loc.id || loc.name} for ${normalizedMarketArea.ma_type}`);
                                const match = findMatchingFeature(loc, features, normalizedMarketArea.ma_type);

                                if (match) {
                                    console.log(`[Import] Found match for ${loc.id || loc.name}:`, match.attributes);
                                    if (match.geometry) {
                                        // Clone geometry to avoid reference issues
                                        const clonedGeometry = JSON.parse(JSON.stringify(match.geometry));
                                        normalizedMarketArea.locations[i] = {
                                            ...loc,
                                            geometry: clonedGeometry
                                        };
                                        matchedCount++;
                                        console.log(`[Import] Added geometry to location: ${loc.name || loc.id}, rings: ${clonedGeometry.rings?.length || 0}`);
                                    } else {
                                        console.warn(`[Import] Match found but NO geometry for ${loc.id || loc.name}`);
                                    }
                                } else {
                                    console.warn(`[Import] No match found for location ${loc.id || loc.name}`);
                                }
                            }
                            console.log(`[Import] Matched ${matchedCount}/${normalizedMarketArea.locations.length} locations for ${normalizedMarketArea.name}`);
                        } else {
                            console.warn(`[Import] No features found for ${normalizedMarketArea.name} (${normalizedMarketArea.ma_type})`);
                        }
                    } catch (queryError) {
                        console.error(`[Import] Error querying features for ${normalizedMarketArea.name}:`, queryError);
                        // Continue without geometries - the API will handle this
                    }
                }

                // Save the market area
                let savedMarketArea;

                // Log the normalized market area for debugging - include geometry info
                const locationsWithGeometry = normalizedMarketArea.locations?.filter(loc => loc.geometry) || [];
                console.log(`Final normalized market area for API submission:`, {
                    id: normalizedMarketArea.id,
                    name: normalizedMarketArea.name,
                    ma_type: normalizedMarketArea.ma_type,
                    style_settings: normalizedMarketArea.style_settings,
                    locations_count: normalizedMarketArea.locations?.length || 0,
                    locations_with_geometry: locationsWithGeometry.length,
                    sample_location: normalizedMarketArea.locations?.[0] ? {
                        id: normalizedMarketArea.locations[0].id,
                        name: normalizedMarketArea.locations[0].name,
                        hasGeometry: !!normalizedMarketArea.locations[0].geometry,
                        ringCount: normalizedMarketArea.locations[0].geometry?.rings?.length || 0
                    } : null
                });

                // Use appropriate function to add market area
                try {
                    savedMarketArea = await marketAreasContext.addMarketArea(
                        effectiveProjectId,
                        normalizedMarketArea
                    );
                    console.log(`Successfully created market area:`, savedMarketArea);
                } catch (apiError) {
                    console.error(`API error creating market area ${marketArea.name}:`, apiError);

                    // Try to extract detailed error messages from the API response
                    let errorMsg = apiError.message || 'API Error';
                    if (apiError.response?.data) {
                        if (typeof apiError.response.data === 'string') {
                            errorMsg = apiError.response.data;
                        } else if (apiError.response.data.detail) {
                            errorMsg = apiError.response.data.detail;
                        } else if (apiError.response.data.message) {
                            errorMsg = apiError.response.data.message;
                        } else if (apiError.response.data.error) {
                            errorMsg = apiError.response.data.error;
                        }
                    }

                    results.errors.push(`${marketArea.name}: ${errorMsg}`);
                    continue;
                }

                if (!savedMarketArea || !savedMarketArea.id) {
                    throw new Error("Failed to create market area - API returned invalid response");
                }

                // Log what the API returned to verify geometry was persisted
                const returnedLocationsWithGeometry = savedMarketArea.locations?.filter(loc => loc.geometry) || [];
                console.log(`[Import] API returned market area:`, {
                    id: savedMarketArea.id,
                    locations_count: savedMarketArea.locations?.length || 0,
                    locations_with_geometry: returnedLocationsWithGeometry.length,
                    sample_returned_location: savedMarketArea.locations?.[0] ? {
                        id: savedMarketArea.locations[0].id,
                        name: savedMarketArea.locations[0].name,
                        hasGeometry: !!savedMarketArea.locations[0].geometry,
                        ringCount: savedMarketArea.locations[0].geometry?.rings?.length || 0
                    } : null
                });

                results.importedCount++;
                results.createdMarketAreaIds.push(savedMarketArea.id);

                // CRITICAL: Merge geometry from normalizedMarketArea into savedMarketArea
                // The API might not return geometry, but we have it from the REST API queries
                if (normalizedMarketArea.locations?.length > 0) {
                    const hasNormalizedGeometry = normalizedMarketArea.locations.some(loc => loc.geometry);
                    const hasReturnedGeometry = savedMarketArea.locations?.some(loc => loc.geometry);

                    if (hasNormalizedGeometry && !hasReturnedGeometry) {
                        console.log(`[Import] API didn't return geometry, using locally fetched geometry for ${savedMarketArea.name}`);
                        savedMarketArea.locations = normalizedMarketArea.locations;
                    }
                }

                // CRITICAL: Set visibility BEFORE visualization to prevent race condition
                // MarketAreaList will clear graphics for market areas not in visibleMarketAreaIds
                if (typeof setVisibleMarketAreaIds === 'function') {
                    const storedVisibleIds = localStorage.getItem(`marketAreas.${effectiveProjectId}.visible`);
                    let currentVisibleIds = storedVisibleIds ? JSON.parse(storedVisibleIds) : [];

                    if (!currentVisibleIds.includes(savedMarketArea.id)) {
                        currentVisibleIds = [...currentVisibleIds, savedMarketArea.id];
                        localStorage.setItem(`marketAreas.${effectiveProjectId}.visible`, JSON.stringify(currentVisibleIds));
                        setVisibleMarketAreaIds(currentVisibleIds);
                        console.log(`[Import] Set visibility for ${savedMarketArea.id}, total visible: ${currentVisibleIds.length}`);

                        // Dispatch event to sync MarketAreaList's local state immediately
                        window.dispatchEvent(new CustomEvent('marketAreasVisibilityChanged', {
                            detail: { visibleIds: currentVisibleIds, projectId: effectiveProjectId }
                        }));
                    }
                }

                // Activate layer and draw graphics based on market area type
                // Visualize the market area with enhanced logging
                console.log(`Visualizing market area with style settings:`, savedMarketArea.style_settings);
                await visualizeMarketArea(savedMarketArea);

            } catch (error) {
                console.error(`Failed to import market area ${marketArea.name}:`, error);
                results.errors.push(`${marketArea.name}: ${error.message}`);
            }
        }
        return results;
    };

    // Visualize a market area based on its type
    const visualizeMarketArea = async (marketArea, forceRefresh = false) => {
        try {
            console.log(`Visualizing market area: ${marketArea.id} (${marketArea.ma_type})`);
    
            // Skip if not a supported type
            if (!SUPPORTED_MARKET_AREA_TYPES.includes(marketArea.ma_type)) {
                console.warn(`Skipping visualization for unsupported type: ${marketArea.ma_type}`);
                return;
            }
    
            // If this is a force refresh, clear any existing graphics for this market area
            if (forceRefresh && selectionGraphicsLayer) {
                const existingGraphics = selectionGraphicsLayer.graphics.filter(
                    g => g.attributes?.marketAreaId === marketArea.id
                );
                
                if (existingGraphics.length > 0) {
                    console.log(`Removing ${existingGraphics.length} existing graphics for market area ${marketArea.id}`);
                    selectionGraphicsLayer.removeMany(existingGraphics);
                }
            }
    
            // Activate the corresponding layer if needed for feature-based market areas
            await addActiveLayer(marketArea.ma_type);
    
            // Process feature-based market areas (zip, county, place)
            if (marketArea.locations && marketArea.locations.length > 0) {
                console.log(`Processing ${marketArea.locations.length} locations for feature-based market area ${marketArea.id}`);
    
                // Query features for location data if geometry is missing
                const locationsWithGeometry = await queryFeaturesForLocations(marketArea);
    
                // Check if we have any features with geometry
                if (locationsWithGeometry.some(loc => loc.geometry)) {
                    // Create feature objects from locations
                    const features = locationsWithGeometry
                        .filter(loc => loc.geometry) // Pre-filter to only include locations with geometry
                        .map(loc => ({
                            geometry: loc.geometry,
                            attributes: {
                                id: loc.id || loc.name,
                                name: loc.name || loc.id,
                                marketAreaId: marketArea.id,
                                FEATURE_TYPE: marketArea.ma_type,
                                order: marketArea.order || 0
                            }
                        }));
    
                    console.log(`Created ${features.length} feature objects with geometry for market area ${marketArea.id}`);
    
                    if (features.length > 0) {
                        // Extract style settings from the market area
                        const styleSettings = marketArea.style_settings || {};
                        
                        // Apply styling to features
                        await updateFeatureStyles(
                            features,
                            {
                                fill: styleSettings.fillColor || "#0078D4",
                                fillOpacity: styleSettings.noFill ? 0 : 
                                    (styleSettings.fillOpacity !== undefined ? styleSettings.fillOpacity : 0.35),
                                outline: styleSettings.borderColor || "#0078D4",
                                outlineWidth: styleSettings.noBorder ? 0 : 
                                    (styleSettings.borderWidth !== undefined ? styleSettings.borderWidth : 2)
                            },
                            marketArea.ma_type,
                            true // Force immediate update
                        );
                    } else {
                        console.warn(`No features with geometry for market area ${marketArea.id}`);
                    }
                } else {
                    console.warn(`No locations with geometry for market area ${marketArea.id}`);
                }
            }
            // Add support for radius and drivetime market areas if needed
            else if (marketArea.ma_type === 'radius' && marketArea.radius_points?.length > 0) {
                // Handle radius market areas
                for (const point of marketArea.radius_points) {
                    if (point && point.center) {
                        await drawRadius(
                            point,
                            marketArea.style_settings || {
                                fillColor: "#0078D4",
                                fillOpacity: 0.35,
                                borderColor: "#0078D4",
                                borderWidth: 2
                            },
                            marketArea.id,
                            marketArea.order || 0
                        );
                    }
                }
            }
            else if (marketArea.ma_type === 'drivetime' && marketArea.drive_time_points?.length > 0) {
                // Handle drive time market areas
                for (const point of marketArea.drive_time_points) {
                    if (point && point.center) {
                        await drawDriveTimePolygon(
                            point,
                            marketArea.style_settings || {
                                fillColor: "#0078D4",
                                fillOpacity: 0.35,
                                borderColor: "#0078D4",
                                borderWidth: 2
                            },
                            marketArea.id,
                            marketArea.order || 0
                        );
                    }
                }
            }
            else {
                console.warn(`Market area ${marketArea.id} has no valid locations to visualize`);
            }
        } catch (error) {
            console.error("Error visualizing market area:", error);
        }
    };

    // Helper function to zoom to created market areas
    const zoomToCreatedMarketAreas = async (marketAreaIds) => {
        if (!marketAreaIds || marketAreaIds.length === 0 || !mapView) return;

        try {
            // Zoom to the first market area with error handling
            if (marketAreaIds.length > 0 && typeof zoomToMarketArea === 'function') {
                try {
                    console.log(`Attempting to zoom to market area: ${marketAreaIds[0]}`);
                    await zoomToMarketArea(marketAreaIds[0]);
                } catch (zoomError) {
                    console.warn(`Error zooming to market area ${marketAreaIds[0]}:`, zoomError);

                    // Fallback to default view of Orange County if zooming fails
                    try {
                        const { default: Point } = await import("@arcgis/core/geometry/Point");

                        const center = new Point({
                            longitude: -117.8311, // Orange County, CA
                            latitude: 33.7175,
                            spatialReference: { wkid: 4326 }
                        });

                        await mapView.goTo({
                            target: center,
                            zoom: 10
                        }, {
                            duration: 800,
                            easing: "ease-in-out"
                        });

                        console.log(`Fallback zoom to Orange County successful`);
                    } catch (fallbackError) {
                        console.error("Error in fallback zoom:", fallbackError);
                    }
                }
            }
        } catch (error) {
            console.error("Error zooming to market areas:", error);
        }
    };

// Enhance the cleanupAfterImport function
const cleanupAfterImport = () => {
    // Reset form state
    resetForm();

    // IMPORTANT: Hide all feature layers to clear any ZIP/Tract/Block boundaries
    // that were shown during the import process
    if (typeof hideAllFeatureLayers === 'function') {
        console.log("Hiding all feature layers after import");
        hideAllFeatureLayers();
    }

    // Clear any selection graphics that may have been created during import
    if (typeof clearMarketAreaGraphics === 'function') {
        console.log("Clearing market area graphics after import");
        clearMarketAreaGraphics();
    }

    // Close the dialog
    onClose();

    // If we were using the map selection, deactivate it temporarily to avoid any conflicts
    if (typeof setIsMapSelectionActive === 'function') {
        setIsMapSelectionActive(false);

        // Re-enable after a short delay
        setTimeout(() => {
            setIsMapSelectionActive(true);
        }, 500);
    }

    // Refresh market areas data if needed
    if (typeof marketAreasContext.fetchMarketAreas === 'function') {
        marketAreasContext.fetchMarketAreas(effectiveProjectId);
    }
};

    // Helper for querying features for locations
    const queryFeaturesForLocations = async (marketArea) => {
        if (!marketArea.locations || marketArea.locations.length === 0) {
            return [];
        }

        // If all locations already have geometry, just return them
        if (marketArea.locations.every(loc => loc.geometry)) {
            return marketArea.locations;
        }

        // Create enhanced locations by querying for missing geometries
        const enhancedLocations = [...marketArea.locations];

        try {
            // Get results from queryFeaturesForMarketArea if available
            const queryResults = await queryFeaturesForMarketArea(marketArea);

            if (queryResults && queryResults.length > 0) {
                console.log(`Found ${queryResults.length} matching features for market area ${marketArea.id} (${marketArea.ma_type})`);

                // Match query results with locations
                for (let i = 0; i < enhancedLocations.length; i++) {
                    const loc = enhancedLocations[i];
                    if (!loc.geometry) {
                        // Try to find a matching feature from query results
                        const matchingFeature = findMatchingFeature(loc, queryResults, marketArea.ma_type);
                        if (matchingFeature) {
                            console.log(`Found matching feature for ${loc.name || loc.id}`);

                            // Clone the geometry to avoid reference issues
                            const clonedGeometry = JSON.parse(JSON.stringify(matchingFeature.geometry));

                            enhancedLocations[i] = {
                                ...loc,
                                geometry: clonedGeometry
                            };
                        } else {
                            console.log(`No matching feature found for ${loc.name || loc.id}`);
                        }
                    }
                }
            } else {
                console.warn(`No query results found for market area ${marketArea.id} (${marketArea.ma_type})`);
            }
        } catch (error) {
            console.warn("Error querying features for locations:", error);
        }

        return enhancedLocations;
    };

    const findMatchingFeature = (location, features, maType) => {
        if (!location || !features || features.length === 0) return null;

        const locId = String(location.id || location.name || '').trim();
        const locName = String(location.name || location.id || '').trim().toLowerCase();

        // Different matching logic based on market area type
        if (maType === 'zip') {
            // ZIP layer uses ZCTA5 field (not ZIP)
            return features.find(feature => {
                const featureZcta = String(feature.attributes.ZCTA5 || feature.attributes.GEOID || '').trim();
                // Compare padded ZIP codes
                const paddedLocId = locId.padStart(5, '0');
                return featureZcta === paddedLocId || featureZcta === locId;
            });
        }
        else if (maType === 'county') {
            const cleanLocName = locName.replace(/\s+county$/i, '');
            return features.find(feature => {
                const featureName = String(feature.attributes.NAME || '').toLowerCase().replace(/\s+county$/i, '').trim();
                return featureName.includes(cleanLocName) || cleanLocName.includes(featureName);
            });
        }
        else if (maType === 'place') {
            return features.find(feature => {
                const featureName = String(feature.attributes.NAME || '').toLowerCase().trim();
                return featureName.includes(locName) || locName.includes(featureName);
            });
        }
        else if (maType === 'tract') {
            // Tract layer uses FIPS (11-digit) and TRACT_FIPS fields
            return features.find(feature => {
                const featureFips = String(feature.attributes.FIPS || '').trim();
                const featureTractFips = String(feature.attributes.TRACT_FIPS || '').trim();
                return featureFips === locId || featureFips.endsWith(locId) ||
                       featureTractFips === locId || locId.endsWith(featureTractFips);
            });
        }
        else if (maType === 'blockgroup') {
            // BlockGroup layer uses FIPS (12-digit) and BLOCKGROUP_FIPS fields
            return features.find(feature => {
                const featureFips = String(feature.attributes.FIPS || '').trim();
                const featureBgFips = String(feature.attributes.BLOCKGROUP_FIPS || '').trim();
                return featureFips === locId || featureFips.endsWith(locId) || featureBgFips === locId;
            });
        }
        else if (maType === 'block') {
            // Block layer uses GEOID (15-digit) and BLOCK fields
            return features.find(feature => {
                const featureGeoid = String(feature.attributes.GEOID || '').trim();
                const featureBlock = String(feature.attributes.BLOCK || '').trim();
                return featureGeoid === locId || featureGeoid.endsWith(locId) || featureBlock === locId;
            });
        }
        else if (maType === 'cbsa') {
            return features.find(feature => {
                const featureGeoid = String(feature.attributes.GEOID || '').trim();
                const featureName = String(feature.attributes.NAME || '').toLowerCase().trim();
                return featureGeoid === locId || featureName.includes(locName) || locName.includes(featureName);
            });
        }
        else if (maType === 'state') {
            return features.find(feature => {
                const featureState = String(feature.attributes.STATE || feature.attributes.GEOID || '').trim();
                const featureName = String(feature.attributes.NAME || '').toLowerCase().trim();
                return featureState === locId || featureName.includes(locName) || locName.includes(featureName);
            });
        }

        // Default matching using ID, GEOID, FIPS, or name
        return features.find(feature => {
            return feature.attributes.OBJECTID === locId ||
                feature.attributes.FID === locId ||
                feature.attributes.GEOID === locId ||
                feature.attributes.FIPS === locId ||
                feature.attributes.ZCTA5 === locId ||
                (feature.attributes.NAME &&
                    String(feature.attributes.NAME).toLowerCase().includes(locName));
        });
    };



    const handleImport = async (confirmedMarketAreas) => {
        setIsProcessing(true);
        const loadingToast = toast.loading("Importing market areas...");
    
        try {
            // Turn off map selection temporarily before we start
            if (typeof setIsMapSelectionActive === 'function') {
                setIsMapSelectionActive(false);
            }
    
            // Store existing graphics we want to keep
            const existingGraphics = storeExistingGraphics();
            console.log(`Preserving ${existingGraphics.length} existing graphics`);
    
            // Clear the selection graphics layer before import to avoid duplicates
            if (selectionGraphicsLayer) {
                selectionGraphicsLayer.removeAll();
            }
    
            // Import all selected market areas
            const importResults = await importAllMarketAreas(confirmedMarketAreas);
    
            // Log visibility state before update
            console.log("Visibility state before update:", localStorage.getItem(`marketAreas.${effectiveProjectId}.visible`));
    
            // Update visibility state with newly created market areas - CRITICAL FOR VISIBILITY ISSUE
            if (importResults.createdMarketAreaIds.length > 0) {
                console.log(`Ensuring visibility for ${importResults.createdMarketAreaIds.length} market areas:`, 
                    importResults.createdMarketAreaIds);
                    
                // Force update the visibility state
                updateVisibilityState(importResults.createdMarketAreaIds);
                
                // Verify the update was applied correctly
                const storedVisibleIds = localStorage.getItem(`marketAreas.${effectiveProjectId}.visible`);
                console.log(`After visibility update, localStorage contains: ${storedVisibleIds}`);
                
                // Direct update to the context's state to force UI refresh
                if (typeof setVisibleMarketAreaIds === 'function') {
                    try {
                        const parsedIds = storedVisibleIds ? JSON.parse(storedVisibleIds) : [];
                        console.log("Directly updating visible market area IDs in context:", parsedIds);
                        setVisibleMarketAreaIds(parsedIds);
                    } catch (e) {
                        console.error("Error parsing visible IDs:", e);
                    }
                }
    
                // Try to zoom to first created market area
                await zoomToCreatedMarketAreas(importResults.createdMarketAreaIds);
            }
    
            // Restore any existing graphics that we want to keep
            await restoreExistingGraphics(existingGraphics);
    
            // Complete the import process
            toast.dismiss(loadingToast);
    
            if (importResults.importedCount > 0) {
                toast.success(`Successfully imported ${importResults.importedCount} market areas`);
    
                if (importResults.errors.length > 0) {
                    console.error("Some market areas failed to import:", importResults.errors);
                    toast.error(`${importResults.errors.length} market areas failed to import. Check console for details.`);
                }
    
                // Make sure we've properly applied styles to all imported market areas
                // This is similar to the MarketAreaForm behavior
                for (const marketAreaId of importResults.createdMarketAreaIds) {
                    try {
                        // Get the market area for visualization
                        const marketArea = await marketAreasContext.getMarketArea(effectiveProjectId, marketAreaId);
                        
                        if (marketArea) {
                            // CRITICAL: Ensure the market area is marked as visible before visualizing
                            marketArea.is_visible = true;
                            
                            // Force refresh visualization with the visibility flag
                            await visualizeMarketArea(marketArea, true);
                            
                            console.log(`Visualized market area ${marketAreaId} with visibility flag set`);
                        }
                    } catch (error) {
                        console.warn(`Error refreshing visualization for market area ${marketAreaId}:`, error);
                    }
                }
    
                // VISIBILITY FIX: Force another UI update after visualization
                if (typeof setVisibleMarketAreaIds === 'function') {
                    try {
                        const storedIds = localStorage.getItem(`marketAreas.${effectiveProjectId}.visible`);
                        const visibleIds = storedIds ? JSON.parse(storedIds) : [];
                        
                        if (visibleIds.length > 0) {
                            console.log("Final visibility update with IDs:", visibleIds);
                            setVisibleMarketAreaIds(visibleIds);
                            
                            // Dispatch a custom event that other components might listen for
                            window.dispatchEvent(new CustomEvent('marketAreasVisibilityChanged', {
                                detail: { visibleIds, projectId: effectiveProjectId }
                            }));
                        }
                    } catch (e) {
                        console.error("Error in final visibility update:", e);
                    }
                }
    
                // Perform cleanup and final refresh
                cleanupAfterImport();
                
                // VISIBILITY FIX: Force fetch market areas after a slight delay
                setTimeout(() => {
                    if (typeof marketAreasContext.fetchMarketAreas === 'function') {
                        console.log("Triggering delayed fetch of market areas to refresh visibility");
                        marketAreasContext.fetchMarketAreas(effectiveProjectId, true);
                    }
                }, 500);
            } else {
                toast.error("Failed to import any market areas");
                if (importResults.errors.length > 0) {
                    toast.error(importResults.errors[0]);
                }
            }
        } catch (error) {
            console.error("Import failed:", error);
            toast.dismiss(loadingToast);
    
            // Improved error message formatting
            let errorMessage = "Import failed";
            if (error?.message) {
                errorMessage += ": " + error.message;
            } else if (error?.response?.data?.detail) {
                errorMessage += ": " + error.response.data.detail;
            }
    
            toast.error(errorMessage);
        } finally {
            setIsProcessing(false);
            setShowPreview(false);

            // IMPORTANT: Always hide feature layers when import process ends
            // This ensures ZIP/Tract/Block boundaries are cleared even on error
            if (typeof hideAllFeatureLayers === 'function') {
                console.log("Finally block: Hiding all feature layers");
                hideAllFeatureLayers();
            }

            // Re-enable map selection after we're done
            if (typeof setIsMapSelectionActive === 'function') {
                setTimeout(() => {
                    setIsMapSelectionActive(true);
                }, 500); // Small delay to ensure everything else is complete
            }
        }
        };



    // Excel file reading function with full style support (using both xlsx and ExcelJS)
    const readExcelFile = (file) => {
        return new Promise(async (resolve, reject) => {
            try {
                const arrayBuffer = await file.arrayBuffer();
                const data = new Uint8Array(arrayBuffer);

                // Use xlsx for data parsing (faster and more compatible)
                const workbook = XLSX.read(data, {
                    type: 'array',
                    cellDates: true,
                    cellStyles: true,
                    cellFormula: true,
                    sheetStubs: true,
                    cellNF: true,
                    cellHTML: false,
                    dense: false
                });

                console.log("XLSX workbook loaded. Sheets:", workbook.SheetNames);

                // Also load with ExcelJS for better color support
                let excelJsWorkbook = null;
                let workbookThemeColors = null;
                try {
                    excelJsWorkbook = new ExcelJS.Workbook();
                    await excelJsWorkbook.xlsx.load(arrayBuffer);
                    console.log("ExcelJS workbook loaded for color extraction");

                    // Try to extract theme colors from the workbook
                    if (excelJsWorkbook.model && excelJsWorkbook.model.themes) {
                        console.log("Workbook themes found:", excelJsWorkbook.model.themes);
                    }

                    // Debug: Check Row 9 colors with ExcelJS
                    const ejsSheet = excelJsWorkbook.worksheets[0];
                    if (ejsSheet) {
                        [4, 6, 8, 10, 12, 14, 16, 18, 20].forEach(colNum => { // D, F, H, J, L, N, P, R, T
                            const cell = ejsSheet.getCell(9, colNum);
                            if (cell && cell.fill) {
                                console.log(`ExcelJS Cell Row9,Col${colNum} fill:`, JSON.stringify(cell.fill, null, 2));
                            }
                            // Also try to get the model color if available
                            if (cell && cell.style && cell.style.fill) {
                                console.log(`ExcelJS Cell Row9,Col${colNum} style.fill:`, JSON.stringify(cell.style.fill, null, 2));
                            }
                        });
                    }

                    // Store theme colors if available
                    workbookThemeColors = excelJsWorkbook.model?.themes;
                } catch (ejsError) {
                    console.warn("ExcelJS loading failed (will use xlsx only):", ejsError.message);
                }

                // Check if xlsx has style information
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                const hasStyles = Object.values(firstSheet).some(cell =>
                    cell && typeof cell === 'object' && cell.s && cell.s.fill);

                console.log(`XLSX style information detected: ${hasStyles ? 'YES' : 'NO'}`);

                if (!hasStyles && !excelJsWorkbook) {
                    console.warn("No cell style information found. Cell colors may not be available.");
                }

                // Convert to JSON
                const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

                // Return data, xlsx workbook, ExcelJS workbook, and theme colors
                resolve({ jsonData, workbook, excelJsWorkbook, workbookThemeColors });
            } catch (error) {
                console.error("Failed to parse Excel file:", error);
                reject(new Error("Failed to parse Excel file: " + error.message));
            }
        });
    };

    // Placeholder for backward compatibility - the actual reading is done above
    const _readExcelFileLegacy = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                // This branch is not used anymore
            };

            reader.onerror = () => {
                reject(new Error("Failed to read file"));
            };

            reader.readAsArrayBuffer(file);
        });
    };

    // Template format detection
    const detectTemplateFormat = (data) => {
        // Look for specific markers in the template
        for (let i = 0; i < Math.min(data.length, 20); i++) {
            const row = data[i];
            if (row && row.length > 0) {
                // Check column A or B for template headers
                const colA = String(row[0] || "").trim();
                const colB = String(row[1] || "").trim();
                const colC = String(row[2] || "").trim();

                if (colA === "Market Area Definitions" ||
                    colB === "Full Market Area Name" ||
                    colB === "Definition Type" ||
                    colC === "Market Name" ||
                    colC === "Short Name" ||
                    colC === "MA Type" ||
                    colC === "State" ||
                    colC === "Fill/Text Color") {
                    console.log(`Template marker found:`, colA || colB || colC);
                    return true;
                }
            }
        }
        return false;
    };


    const processTemplateData = (data, workbook, excelJsWorkbook = null) => {
        // All market area types are now supported for import
        const SUPPORTED_IMPORT_TYPES = [
            'zip', 'place', 'county', 'tract', 'blockgroup', 'block',
            'cbsa', 'state', 'radius', 'drivetime', 'custom', 'site_location', 'usa'
        ];

        console.log("Processing template format data with support for all market area types");

        // Define exact row indices based on NEW Excel template structure (0-based)
        // Row 5 = Market Name, Row 7 = Short Name, Row 9 = Fill/Text Color
        // Row 11 = MA Type, Row 13 = State, Row 15 = County, Row 17 = Notes
        // Row 21+ = Location values
        const MARKET_AREA_NAME_ROW = 4;  // Excel Row 5
        const SHORT_NAME_ROW = 6;        // Excel Row 7
        const COLOR_ROW = 8;             // Excel Row 9 - Fill and Text color combined
        const DEFINITION_TYPE_ROW = 10;  // Excel Row 11 (MA Type)
        const STATE_ROW = 12;            // Excel Row 13
        const COUNTY_ROW = 14;           // Excel Row 15
        const NOTES_ROW = 16;            // Excel Row 17
        const DEFINITION_START_ROW = 20; // Excel Row 21 (first definition value)

        // Legacy row indices for backward compatibility with old templates
        const LEGACY_FILL_COLOR_ROW = 23;       // Excel Row 24
        const LEGACY_TRANSPARENCY_ROW = 24;     // Excel Row 25
        const LEGACY_BORDER_COLOR_ROW = 26;     // Excel Row 27
        const LEGACY_BORDER_WEIGHT_ROW = 27;    // Excel Row 28
        const LEGACY_DEFINITION_START_ROW = 29; // Excel Row 30

        // Validate that we have enough data rows
        if (data.length < DEFINITION_START_ROW) {
            console.warn("Excel file doesn't have enough rows for template format");
            return [];
        }

        // Excel indexed color palette (standard 56 colors)
        const INDEXED_COLORS = [
            '000000', 'FFFFFF', 'FF0000', '00FF00', '0000FF', 'FFFF00', 'FF00FF', '00FFFF',
            '000000', 'FFFFFF', 'FF0000', '00FF00', '0000FF', 'FFFF00', 'FF00FF', '00FFFF',
            '800000', '008000', '000080', '808000', '800080', '008080', 'C0C0C0', '808080',
            '9999FF', '993366', 'FFFFCC', 'CCFFFF', '660066', 'FF8080', '0066CC', 'CCCCFF',
            '000080', 'FF00FF', 'FFFF00', '00FFFF', '800080', '800000', '008080', '0000FF',
            '00CCFF', 'CCFFFF', 'CCFFCC', 'FFFF99', '99CCFF', 'FF99CC', 'CC99FF', 'FFCC99',
            '3366FF', '33CCCC', '99CC00', 'FFCC00', 'FF9900', 'FF6600', '666699', '969696',
            '003366', '339966', '003300', '333300', '993300', '993366', '333399', '333333'
        ];

        // Helper function to apply tint to a hex color (Excel tint calculation)
        const applyTint = (hexColor, tint) => {
            if (tint === 0 || tint === undefined || tint === null) return hexColor;

            // Parse hex to RGB
            const hex = hexColor.replace('#', '');
            let r = parseInt(hex.substring(0, 2), 16);
            let g = parseInt(hex.substring(2, 4), 16);
            let b = parseInt(hex.substring(4, 6), 16);

            if (tint < 0) {
                // Negative tint = darken (mix toward black)
                const factor = 1 + tint; // tint is negative, so this reduces the value
                r = Math.round(r * factor);
                g = Math.round(g * factor);
                b = Math.round(b * factor);
            } else {
                // Positive tint = lighten (mix toward white)
                r = Math.round(r + (255 - r) * tint);
                g = Math.round(g + (255 - g) * tint);
                b = Math.round(b + (255 - b) * tint);
            }

            // Clamp values to valid range
            r = Math.max(0, Math.min(255, r));
            g = Math.max(0, Math.min(255, g));
            b = Math.max(0, Math.min(255, b));

            // Convert back to hex
            return `#${r.toString(16).padStart(2, '0').toUpperCase()}${g.toString(16).padStart(2, '0').toUpperCase()}${b.toString(16).padStart(2, '0').toUpperCase()}`;
        };

        // Try to extract theme colors from the workbook
        const getWorkbookThemeColors = () => {
            if (!excelJsWorkbook) return null;
            try {
                // ExcelJS stores theme in workbook model
                const model = excelJsWorkbook.model;
                // Themes can be stored as object {theme1: xml} or array [xml]
                const hasThemes = model && model.themes && (model.themes.theme1 || (Array.isArray(model.themes) && model.themes.length > 0));
                if (hasThemes) {
                    // Parse theme XML to extract colors - handle both formats
                    const themeXml = model.themes.theme1 || model.themes[0];
                    console.log("Theme XML found, length:", themeXml?.length);

                    // Try to extract colors from theme XML using regex
                    const colors = {};

                    // Look for clrScheme colors in the theme XML
                    // Format: <a:dk1><a:sysClr val="windowText" lastClr="000000"/></a:dk1>
                    // Or: <a:accent1><a:srgbClr val="4472C4"/></a:accent1>
                    const colorMappings = [
                        { name: 'lt1', index: 0 },  // Light 1
                        { name: 'dk1', index: 1 },  // Dark 1
                        { name: 'lt2', index: 2 },  // Light 2
                        { name: 'dk2', index: 3 },  // Dark 2
                        { name: 'accent1', index: 4 },
                        { name: 'accent2', index: 5 },
                        { name: 'accent3', index: 6 },
                        { name: 'accent4', index: 7 },
                        { name: 'accent5', index: 8 },
                        { name: 'accent6', index: 9 }
                    ];

                    for (const mapping of colorMappings) {
                        // Try srgbClr format first
                        const srgbMatch = themeXml.match(new RegExp(`<a:${mapping.name}[^>]*>.*?<a:srgbClr[^>]*val="([A-Fa-f0-9]{6})"`, 's'));
                        if (srgbMatch) {
                            colors[mapping.index] = `#${srgbMatch[1].toUpperCase()}`;
                            continue;
                        }

                        // Try sysClr with lastClr
                        const sysClrMatch = themeXml.match(new RegExp(`<a:${mapping.name}[^>]*>.*?<a:sysClr[^>]*lastClr="([A-Fa-f0-9]{6})"`, 's'));
                        if (sysClrMatch) {
                            colors[mapping.index] = `#${sysClrMatch[1].toUpperCase()}`;
                        }
                    }

                    if (Object.keys(colors).length > 0) {
                        console.log("Extracted theme colors from workbook:", colors);
                        return colors;
                    }
                }
                return null;
            } catch (error) {
                console.warn("Error extracting theme colors:", error);
                return null;
            }
        };

        // Get theme colors (try workbook theme first, fall back to Office defaults)
        const extractedThemeColors = getWorkbookThemeColors();
        const themeColors = extractedThemeColors || {
            0: '#FFFFFF', // lt1 - Light 1 (white)
            1: '#000000', // dk1 - Dark 1 (black)
            2: '#E7E6E6', // lt2 - Light 2
            3: '#44546A', // dk2 - Dark 2
            4: '#4472C4', // accent1 - Blue
            5: '#ED7D31', // accent2 - Orange
            6: '#A5A5A5', // accent3 - Gray
            7: '#FFC000', // accent4 - Gold
            8: '#5B9BD5', // accent5 - Light Blue
            9: '#70AD47'  // accent6 - Green
        };
        console.log("Using theme colors:", themeColors);

        // Helper function to extract color from ExcelJS cell fill
        const getExcelJsColor = (row, col) => {
            if (!excelJsWorkbook) return null;
            try {
                const ejsSheet = excelJsWorkbook.worksheets[0];
                if (!ejsSheet) return null;

                // ExcelJS uses 1-based indices
                const cell = ejsSheet.getCell(row + 1, col + 1);
                if (!cell || !cell.fill) return null;

                const fill = cell.fill;
                console.log(`ExcelJS color check for Row${row + 1},Col${col + 1}:`, JSON.stringify(fill, null, 2));

                if (fill.type === 'pattern' && fill.fgColor) {
                    // ARGB format (8 chars) - skip first 2 chars (alpha)
                    if (fill.fgColor.argb) {
                        const argb = fill.fgColor.argb;
                        const rgb = argb.length === 8 ? argb.substring(2) : argb;
                        if (rgb !== '000000' && rgb !== 'FFFFFF') {
                            console.log(`ExcelJS found ARGB color: #${rgb}`);
                            return `#${rgb}`;
                        }
                    }
                    // Theme color with tint
                    if (fill.fgColor.theme !== undefined) {
                        const baseColor = themeColors[fill.fgColor.theme];
                        if (baseColor) {
                            const tint = fill.fgColor.tint || 0;
                            const finalColor = applyTint(baseColor, tint);
                            console.log(`ExcelJS theme color: theme=${fill.fgColor.theme}, tint=${tint}, base=${baseColor}, final=${finalColor}`);
                            return finalColor;
                        }
                    }
                }
                return null;
            } catch (error) {
                console.warn(`Error getting ExcelJS color at Row${row + 1},Col${col + 1}:`, error);
                return null;
            }
        };

        // Helper function to extract font/text color from ExcelJS cell
        const getCellFontColor = (row, col) => {
            if (!excelJsWorkbook) return null;
            try {
                const ejsSheet = excelJsWorkbook.worksheets[0];
                if (!ejsSheet) return null;

                // ExcelJS uses 1-based indices
                const cell = ejsSheet.getCell(row + 1, col + 1);
                if (!cell || !cell.font || !cell.font.color) return null;

                const fontColor = cell.font.color;
                console.log(`ExcelJS font color check for Row${row + 1},Col${col + 1}:`, JSON.stringify(fontColor, null, 2));

                // ARGB format (8 chars) - skip first 2 chars (alpha)
                if (fontColor.argb) {
                    const argb = fontColor.argb;
                    const rgb = argb.length === 8 ? argb.substring(2) : argb;
                    console.log(`ExcelJS found font ARGB color: #${rgb}`);
                    return `#${rgb}`;
                }

                // Theme color with tint
                if (fontColor.theme !== undefined) {
                    const baseColor = themeColors[fontColor.theme];
                    if (baseColor) {
                        const tint = fontColor.tint || 0;
                        const finalColor = applyTint(baseColor, tint);
                        console.log(`ExcelJS font theme color: theme=${fontColor.theme}, tint=${tint}, base=${baseColor}, final=${finalColor}`);
                        return finalColor;
                    }
                }

                return null;
            } catch (error) {
                console.warn(`Error getting font color at Row${row + 1},Col${col + 1}:`, error);
                return null;
            }
        };

        // Enhanced helper function to get cell background color from workbook
        const getCellBackgroundColor = (row, col) => {
            // Try ExcelJS first (more reliable for colors)
            const excelJsColor = getExcelJsColor(row, col);
            if (excelJsColor) {
                return excelJsColor;
            }

            // Fall back to xlsx
            if (!workbook) return null;
            try {
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const colLetter = XLSX.utils.encode_col(col);
                const rowNumber = row + 1;
                const cellAddress = `${colLetter}${rowNumber}`;
                const cell = sheet[cellAddress];

                if (!cell) { return null; }

                // Debug: Log the full cell structure for Row 9
                if (row === 8) {
                    console.log(`XLSX Cell ${cellAddress} full structure:`, JSON.stringify(cell, null, 2));
                }

                if (cell.s && cell.s.fill) {
                    const fill = cell.s.fill;

                    // Try fgColor first (most common)
                    if (fill.fgColor) {
                        // RGB format
                        if (fill.fgColor.rgb && fill.fgColor.rgb !== '00000000' && fill.fgColor.rgb !== 'FFFFFFFF') {
                            const rgb = fill.fgColor.rgb;
                            return `#${rgb.substring(rgb.length - 6)}`;
                        }
                        // Indexed color format
                        if (fill.fgColor.indexed !== undefined && fill.fgColor.indexed < INDEXED_COLORS.length) {
                            const color = INDEXED_COLORS[fill.fgColor.indexed];
                            if (color && color !== '000000' && color !== 'FFFFFF') {
                                return `#${color}`;
                            }
                        }
                        // Theme color (we'll use a basic mapping)
                        if (fill.fgColor.theme !== undefined) {
                            // Theme colors need the workbook's theme, but we can use defaults
                            console.log(`Theme color detected: theme=${fill.fgColor.theme}, tint=${fill.fgColor.tint}`);
                        }
                    }

                    // Try bgColor as fallback
                    if (fill.bgColor) {
                        if (fill.bgColor.rgb && fill.bgColor.rgb !== '00000000' && fill.bgColor.rgb !== 'FFFFFFFF') {
                            const rgb = fill.bgColor.rgb;
                            return `#${rgb.substring(rgb.length - 6)}`;
                        }
                        if (fill.bgColor.indexed !== undefined && fill.bgColor.indexed < INDEXED_COLORS.length) {
                            const color = INDEXED_COLORS[fill.bgColor.indexed];
                            if (color && color !== '000000' && color !== 'FFFFFF') {
                                return `#${color}`;
                            }
                        }
                    }
                }

                return null; // No specific background color found
            } catch (error) {
                console.warn(`Error getting cell background color at ${row + 1}, ${col}:`, error);
                return null;
            }
        };

        // Find all market area columns
        const marketAreaColumns = [];
        const maxCol = data.reduce((max, row) => Math.max(max, row ? row.length : 0), 0); // Find max columns across all rows

        // Check headers in specific rows to find columns
        const nameRowData = data[MARKET_AREA_NAME_ROW] || [];
        const typeRowData = data[DEFINITION_TYPE_ROW] || [];

        for (let col = 3; col < maxCol; col += 2) { // Start from Col D, step by 2
            const marketAreaName = String(nameRowData[col] || "").trim();
            const definitionType = String(typeRowData[col] || "").trim();

            if (marketAreaName && definitionType) {
                const maType = mapDefinitionTypeToMAType(definitionType); // Returns null if unsupported

                // --- Filter for Supported Import Types ---
                if (maType && SUPPORTED_IMPORT_TYPES.includes(maType)) {
                    console.log(`Found supported market area for import in column ${col + 1} (${String.fromCharCode(65 + col)}): ${marketAreaName} (${definitionType} -> ${maType})`);
                    marketAreaColumns.push(col);
                } else {
                    console.log(`Skipping unsupported market area in column ${col + 1} (${String.fromCharCode(65 + col)}): ${marketAreaName} (${definitionType} -> ${maType || 'unsupported'})`);
                }
                // --- End Filter ---
            }
        }

        console.log(`Found ${marketAreaColumns.length} supported market areas in template columns: ${marketAreaColumns.map(col => String.fromCharCode(65 + col)).join(', ')}`);

        if (marketAreaColumns.length === 0) {
            console.warn("No supported market areas found in the template columns.");
            return [];
        }

        // Process each market area column
        const marketAreas = [];
        for (const col of marketAreaColumns) {
            const marketAreaName = String(nameRowData[col] || "").trim();
            const definitionType = String(typeRowData[col] || "").trim();
            const maType = mapDefinitionTypeToMAType(definitionType); // Already verified as supported

            console.log(`Processing market area in column ${col + 1} (${String.fromCharCode(65 + col)}): ${marketAreaName} (${definitionType} -> ${maType})`);

            const shortName = (data[SHORT_NAME_ROW] ? String(data[SHORT_NAME_ROW][col] || "").trim() : "") || marketAreaName.substring(0, 20);
            const state = (data[STATE_ROW] ? String(data[STATE_ROW][col] || "").trim() : "") || "CA"; // Default state if empty
            // County field might be relevant for filtering later, but not directly used for location definition here
            // const county = data[COUNTY_ROW] ? String(data[COUNTY_ROW][col] || "").trim() : "";

            // --- Style Extraction Logic ---
            const styleSettings = {
                fillColor: "#0078D4", fillOpacity: 0.35, borderColor: "#0078D4",
                borderWidth: 2, noFill: false, noBorder: false, themeName: "Default",
                textColor: "#FFFFFF", excelFill: "#ffffff", excelText: "#000000"
            };

            // Try new format first (Row 9 has combined fill/text color)
            const colorCellBackgroundColor = getCellBackgroundColor(COLOR_ROW, col);
            const colorCellFontColor = getCellFontColor(COLOR_ROW, col);
            const colorTextValue = (data[COLOR_ROW] && data[COLOR_ROW][col] != null) ? String(data[COLOR_ROW][col]).trim() : "";

            // Extract fill color from cell background
            if (colorCellBackgroundColor) {
                styleSettings.fillColor = colorCellBackgroundColor;
                styleSettings.borderColor = colorCellBackgroundColor;
                console.log(`Using fill color from Row 9 cell background: ${colorCellBackgroundColor}`);
            }

            // Extract text color from cell font
            if (colorCellFontColor) {
                styleSettings.textColor = colorCellFontColor;
                console.log(`Using text color from Row 9 cell font: ${colorCellFontColor}`);
            }

            // Try to parse text value as fallback (for "Fill: #XXXXX / Text: #XXXXX" format)
            if (colorTextValue && colorTextValue !== "") {
                const fillMatch = colorTextValue.match(/Fill:\s*(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3})/i);
                const textMatch = colorTextValue.match(/Text:\s*(#[0-9A-Fa-f]{6}|#[0-9A-Fa-f]{3})/i);

                // Use text value as fallback if cell styling didn't provide colors
                if (fillMatch && styleSettings.fillColor === "#0078D4") {
                    styleSettings.fillColor = fillMatch[1];
                    styleSettings.borderColor = fillMatch[1];
                    console.log(`Using fill color from text value: ${fillMatch[1]}`);
                }
                if (textMatch && styleSettings.textColor === "#FFFFFF") {
                    styleSettings.textColor = textMatch[1];
                    console.log(`Using text color from text value: ${textMatch[1]}`);
                }

                // Fall back to parsing as plain hex color for fill
                if (!fillMatch && !textMatch && styleSettings.fillColor === "#0078D4") {
                    const parsedColor = ensureHexColor(colorTextValue);
                    if (parsedColor) {
                        styleSettings.fillColor = parsedColor;
                        styleSettings.borderColor = parsedColor;
                        console.log(`Using plain hex color from Row 9 text: ${parsedColor}`);
                    }
                }
            }

            // Fall back to legacy format if no color found in new location
            if (styleSettings.fillColor === "#0078D4") {
                // Fill Color from legacy row
                const fillCellBackgroundColor = getCellBackgroundColor(LEGACY_FILL_COLOR_ROW, col);
                const fillTextValue = (data[LEGACY_FILL_COLOR_ROW] && data[LEGACY_FILL_COLOR_ROW][col] != null) ? String(data[LEGACY_FILL_COLOR_ROW][col]).trim() : "";
                if (fillTextValue.toLowerCase() === "no fill") {
                    styleSettings.noFill = true; styleSettings.fillOpacity = 0;
                } else if (fillCellBackgroundColor) {
                    styleSettings.fillColor = fillCellBackgroundColor;
                } else if (fillTextValue && fillTextValue.toLowerCase() !== "text color") {
                    styleSettings.fillColor = ensureHexColor(fillTextValue);
                }

                // Transparency/Opacity from legacy row
                const transparencyValue = (data[LEGACY_TRANSPARENCY_ROW] && data[LEGACY_TRANSPARENCY_ROW][col] != null) ? String(data[LEGACY_TRANSPARENCY_ROW][col]).trim() : "";
                if (!styleSettings.noFill && transparencyValue) {
                    if (transparencyValue.toLowerCase() === "no fill") {
                        styleSettings.noFill = true; styleSettings.fillOpacity = 0;
                    } else if (transparencyValue.endsWith("%")) {
                        const transparencyPercent = parseInt(transparencyValue);
                        if (!isNaN(transparencyPercent)) { styleSettings.fillOpacity = Math.max(0, Math.min(1, (100 - transparencyPercent) / 100)); }
                    } else {
                        let opacity = Number(transparencyValue);
                        if (!isNaN(opacity)) {
                            if (opacity > 1 && opacity <= 100) { opacity = (100 - opacity) / 100; }
                            styleSettings.fillOpacity = Math.max(0, Math.min(1, opacity));
                        }
                    }
                    if (styleSettings.fillOpacity === 0) { styleSettings.noFill = true; }
                }

                // Border Color from legacy row
                const borderCellBackgroundColor = getCellBackgroundColor(LEGACY_BORDER_COLOR_ROW, col);
                const borderTextValue = (data[LEGACY_BORDER_COLOR_ROW] && data[LEGACY_BORDER_COLOR_ROW][col] != null) ? String(data[LEGACY_BORDER_COLOR_ROW][col]).trim() : "";
                if (borderTextValue.toLowerCase() === "no border") {
                    styleSettings.noBorder = true; styleSettings.borderWidth = 0;
                } else if (borderCellBackgroundColor) {
                    styleSettings.borderColor = borderCellBackgroundColor;
                } else if (borderTextValue && borderTextValue.toLowerCase() !== "text color") {
                    styleSettings.borderColor = ensureHexColor(borderTextValue);
                }

                // Border Width from legacy row
                const borderWidthValue = (data[LEGACY_BORDER_WEIGHT_ROW] && data[LEGACY_BORDER_WEIGHT_ROW][col] != null) ? String(data[LEGACY_BORDER_WEIGHT_ROW][col]).trim() : "";
                if (!styleSettings.noBorder && borderWidthValue) {
                    if (borderWidthValue.toLowerCase() === "no border") {
                        styleSettings.noBorder = true; styleSettings.borderWidth = 0;
                    } else if (!isNaN(parseFloat(borderWidthValue))) {
                        styleSettings.borderWidth = Math.max(0, Number(borderWidthValue));
                    }
                    if (styleSettings.borderWidth === 0) { styleSettings.noBorder = true; }
                }
            }
            // --- End Style Extraction ---

            // Get notes from Row 17 (new format)
            const notes = (data[NOTES_ROW] && data[NOTES_ROW][col] != null) ? String(data[NOTES_ROW][col]).trim() : "";


            // Extract Definition Values
            const definitionValues = [];
            for (let i = DEFINITION_START_ROW; i < data.length; i++) {
                if (!data[i] || data[i][col] == null) continue; // Check for null/undefined
                const rawValue = data[i][col];
                const value = String(rawValue).trim();

                if (value && !value.startsWith("*")) { // Simple check to exclude helper text
                    // Split potentially delimited values within a single cell
                    const potentialValues = value.split(/[,;\n]+/).map(v => v.trim()).filter(Boolean);
                    for (const pv of potentialValues) {
                        // Add basic type validation if needed (e.g., ensure ZIPs look like ZIPs)
                        if (maType === 'zip' && !/^\d{5}(-\d{4})?$/.test(pv)) {
                            console.warn(`Skipping invalid ZIP format "${pv}" in column ${col + 1}, row ${i + 1}`);
                            continue;
                        }
                        if ((maType === 'county' || maType === 'place') && /^\d+$/.test(pv)) {
                            console.warn(`Skipping numeric-only value "${pv}" for ${maType} in column ${col + 1}, row ${i + 1}`);
                            continue;
                        }
                         if (!definitionValues.includes(pv)) { // Avoid duplicates
                            definitionValues.push(pv);
                        }
                    }
                }
            }

            console.log(`Found ${definitionValues.length} definition values for ${marketAreaName}: ${definitionValues.slice(0, 10).join(', ')}${definitionValues.length > 10 ? '...' : ''}`);

            // Skip if no valid definition values found
            if (definitionValues.length === 0) {
                console.warn(`No valid definition values found for market area ${marketAreaName} in column ${col + 1}, skipping.`);
                continue;
            }

            // Create the market area object
            const marketArea = {
                name: marketAreaName,
                short_name: shortName,
                ma_type: maType,
                description: `Imported from ${fileName} (template)`,
                style_settings: styleSettings,
                project: effectiveProjectId,
                project_id: effectiveProjectId,
                locations: definitionValues.map(val => ({
                    id: val, // Use the value itself as ID for zip/county/place name
                    name: val, // Use the value as name
                    state: state // Assign the state read from the template header
                }))
            };

            console.log(`Processed column ${col + 1} into market area:`, { name: marketArea.name, type: marketArea.ma_type, locCount: marketArea.locations.length, style: marketArea.style_settings });
            marketAreas.push(marketArea);
        }

        console.log(`Successfully created ${marketAreas.length} supported market areas from template`);
        return marketAreas;
    };



    // Enhanced definition type mapping - supports all market area types
    const mapDefinitionTypeToMAType = (definitionType) => {
        if (!definitionType) return null;

        // Normalize the input for consistent comparison
        const normalizedType = definitionType.toUpperCase().trim();

        // ZIP type mappings
        if (normalizedType.includes('ZIP') || normalizedType.includes('ZCTA')) {
            return 'zip';
        }

        // County type mappings
        if (normalizedType.includes('COUNTY')) {
            return 'county';
        }

        // Place type mappings
        if (normalizedType.includes('PLACE') || normalizedType.includes('CITY') || normalizedType.includes('TOWN')) {
            return 'place';
        }

        // Census Tract mappings
        if (normalizedType.includes('TRACT') || normalizedType.includes('CENSUS TRACT')) {
            return 'tract';
        }

        // Census Block Group mappings
        if (normalizedType.includes('BLOCK GROUP') || normalizedType.includes('BLOCKGROUP') || normalizedType.includes('BG')) {
            return 'blockgroup';
        }

        // Census Block mappings (must come after block group check)
        if (normalizedType.includes('BLOCK') && !normalizedType.includes('GROUP')) {
            return 'block';
        }

        // CBSA (Core Based Statistical Area) mappings
        if (normalizedType.includes('CBSA') || normalizedType.includes('MSA') || normalizedType.includes('METRO')) {
            return 'cbsa';
        }

        // State mappings
        if (normalizedType.includes('STATE')) {
            return 'state';
        }

        // Radius mappings
        if (normalizedType.includes('RADIUS') || normalizedType.includes('MILE') || normalizedType.includes('RING')) {
            return 'radius';
        }

        // Drive Time mappings
        if (normalizedType.includes('DRIVE') || normalizedType.includes('DRIVETIME') || normalizedType.includes('ISOCHRONE') || normalizedType.includes('MINUTE')) {
            return 'drivetime';
        }

        // Custom mappings
        if (normalizedType.includes('CUSTOM') || normalizedType.includes('POLYGON') || normalizedType.includes('SHAPE')) {
            return 'custom';
        }

        // Site Location mappings
        if (normalizedType.includes('SITE') || normalizedType.includes('LOCATION') || normalizedType.includes('POINT')) {
            return 'site_location';
        }

        // USA mappings
        if (normalizedType.includes('USA') || normalizedType.includes('NATIONAL') || normalizedType === 'US') {
            return 'usa';
        }

        // Return null for any other type (will be filtered out)
        return null;
    };

    // Process standard format with color support
    const processStandardData = (data, workbook, excelJsWorkbook = null) => {
        // All market area types are now supported for import
        const SUPPORTED_IMPORT_TYPES = [
            'zip', 'place', 'county', 'tract', 'blockgroup', 'block',
            'cbsa', 'state', 'radius', 'drivetime', 'custom', 'site_location', 'usa'
        ];

        // Check if we have data with at least 2 rows (headers + data)
        if (!data || data.length < 2) {
            throw new Error("Invalid data format: The Excel file must have headers and at least one row of data");
        }

        console.log("Processing standard format data:", data);
        const headers = data[0].map(h => String(h || '').trim().toLowerCase()); // Normalize headers

        // Helper function to extract color from ExcelJS cell fill
        const getExcelJsColor = (row, col) => {
            if (!excelJsWorkbook) return null;
            try {
                const ejsSheet = excelJsWorkbook.worksheets[0];
                if (!ejsSheet) return null;
                const cell = ejsSheet.getCell(row + 1, col + 1);
                if (!cell || !cell.fill) return null;
                const fill = cell.fill;
                if (fill.type === 'pattern' && fill.fgColor) {
                    if (fill.fgColor.argb) {
                        const argb = fill.fgColor.argb;
                        const rgb = argb.length === 8 ? argb.substring(2) : argb;
                        if (rgb !== '000000' && rgb !== 'FFFFFF') {
                            return `#${rgb}`;
                        }
                    }
                }
                return null;
            } catch (error) { return null; }
        };

        // Helper function to get cell background color from workbook
        const getCellBackgroundColor = (row, col) => {
            // Try ExcelJS first
            const excelJsColor = getExcelJsColor(row, col);
            if (excelJsColor) return excelJsColor;

            // Fall back to xlsx
            if (!workbook) return null;
            try {
                const sheet = workbook.Sheets[workbook.SheetNames[0]];
                const colLetter = XLSX.utils.encode_col(col);
                const rowNumber = row + 1; // Data starts at row 1 in sheet (Excel row 2)
                const cellAddress = `${colLetter}${rowNumber}`;
                if (sheet[cellAddress] && sheet[cellAddress].s && sheet[cellAddress].s.fill) {
                    const fill = sheet[cellAddress].s.fill;
                    if (fill.fgColor && fill.fgColor.rgb) { return `#${fill.fgColor.rgb.substring(fill.fgColor.rgb.length - 6)}`; }
                    if (fill.bgColor && fill.bgColor.rgb) { return `#${fill.bgColor.rgb.substring(fill.bgColor.rgb.length - 6)}`; }
                } return null;
            } catch (error) { console.warn("Error getting cell background color:", error); return null; }
        };

        // Find key columns and their indices (case-insensitive)
        const findIndex = (possibleHeaders) => headers.findIndex(h => possibleHeaders.some(ph => h.includes(ph)));

        const nameIndex = findIndex(['name']);
        const typeIndex = findIndex(['type', 'ma_type']);
        const fillColorIndex = findIndex(['fill color', 'fillcolor']);
        const borderColorIndex = findIndex(['border color', 'bordercolor', 'outline color']);
        const borderWidthIndex = findIndex(['border width', 'borderwidth', 'outline width']);
        const fillOpacityIndex = findIndex(['opacity', 'transparency']);
        const locationsIndex = findIndex(['locations', 'areas', 'definition', 'values']);
        const stateIndex = findIndex(['state', 'st']); // Look for 'st' as well

        // Ensure we have the minimum required fields
        if (nameIndex === -1 || typeIndex === -1) {
            console.error("Headers found:", headers);
            throw new Error("Required columns missing: The Excel file must include 'Name' and 'Type' columns (case-insensitive).");
        }

        const marketAreas = [];

        // Process each row (skip header row 0)
        for (let i = 1; i < data.length; i++) {
            const row = data[i];

            // Skip empty rows or rows missing required data
            if (!row || !row.length || !row[nameIndex] || !row[typeIndex]) {
                 console.log(`Skipping empty or incomplete row ${i + 1}`);
                 continue;
            }

            console.log(`Processing Excel row ${i + 1}:`, row);

            // Extract state information if available, default to CA
            const state = (stateIndex !== -1 && row[stateIndex]) ? String(row[stateIndex]).trim() : "CA";

            // Determine the market area type and normalize it
            const maTypeRaw = String(row[typeIndex]);
            const maType = normalizeMarketAreaType(maTypeRaw); // Returns null for unsupported

            // --- Explicitly skip unsupported types for IMPORT ---
            if (!maType || !SUPPORTED_IMPORT_TYPES.includes(maType)) {
                console.log(`Skipping row ${i + 1}: Unsupported or invalid market area type "${maTypeRaw}" (Normalized: ${maType}) for import.`);
                continue;
            }
            // --- End explicit check ---

            // Extract locations data if available
            let locations = [];
            if (locationsIndex !== -1 && row[locationsIndex] != null) { // Check for null/undefined explicitly
                try {
                    const locationData = row[locationsIndex];
                    let parsedLocations = [];

                    if (typeof locationData === 'string') {
                        if (locationData.trim().startsWith('[')) { // Crude check for JSON array
                           try { parsedLocations = JSON.parse(locationData); } catch { /* ignore parse error */ }
                        }
                        if (parsedLocations.length === 0) { // If not JSON or parse failed, treat as delimited
                           parsedLocations = locationData.split(/[,;\n]+/).map(loc => loc.trim()).filter(Boolean);
                        }
                    } else if (Array.isArray(locationData)) {
                        parsedLocations = locationData;
                    } else if (typeof locationData === 'number') { // Handle single number case
                        parsedLocations = [String(locationData)];
                    }

                    // Convert parsed data into standard {id, name, state} format
                    locations = parsedLocations.map(loc => {
                        if (typeof loc === 'object' && loc !== null && (loc.id || loc.name)) {
                             // Assume it's already in {id, name} format, add state
                            return { id: String(loc.id || loc.name), name: String(loc.name || loc.id), state: loc.state || state };
                        } else {
                            // Assume it's a simple value (string/number)
                            const valStr = String(loc).trim();
                            return { id: valStr, name: valStr, state: state };
                        }
                    }).filter(loc => loc.id); // Ensure ID exists

                } catch (error) {
                    console.warn(`Failed to parse locations for row ${i + 1}:`, error);
                }
            }
             // If locations array is still empty after parsing or no column found,
             // create a single location based on the market area name and type.
             if (locations.length === 0 && SUPPORTED_IMPORT_TYPES.includes(maType)) {
                 const nameVal = String(row[nameIndex]);
                 locations = [{ id: nameVal, name: nameVal, state: state }];
                 console.log(`No locations found or parsed for row ${i+1}, using market area name "${nameVal}" as the single location.`);
             }


            // Create style settings object with defaults
            const styleSettings = {
                fillColor: "#0078D4", fillOpacity: 0.35, borderColor: "#0078D4",
                borderWidth: 2, noFill: false, noBorder: false, themeName: "Default", // Added themeName default
                excelFill: "#ffffff", excelText: "#000000" // Added Excel defaults
            };

            // Extract styles (Fill Color)
            if (fillColorIndex !== -1) {
                const cellBgColor = getCellBackgroundColor(i, fillColorIndex);
                const textValue = row[fillColorIndex] != null ? String(row[fillColorIndex]).trim() : ""; // Use != null check
                if (textValue.toLowerCase() === "no fill") {
                    styleSettings.noFill = true; styleSettings.fillOpacity = 0;
                } else if (cellBgColor) {
                    styleSettings.fillColor = cellBgColor;
                } else if (textValue && textValue.toLowerCase() !== "text color") {
                    styleSettings.fillColor = ensureHexColor(textValue);
                }
            }

            // Extract styles (Border Color)
            if (borderColorIndex !== -1) {
                const cellBgColor = getCellBackgroundColor(i, borderColorIndex);
                const textValue = row[borderColorIndex] != null ? String(row[borderColorIndex]).trim() : "";
                if (textValue.toLowerCase() === "no border") {
                    styleSettings.noBorder = true; styleSettings.borderWidth = 0;
                } else if (cellBgColor) {
                    styleSettings.borderColor = cellBgColor;
                } else if (textValue && textValue.toLowerCase() !== "text color") {
                    styleSettings.borderColor = ensureHexColor(textValue);
                }
            }

            // Extract styles (Opacity/Transparency) - handle only if not already 'noFill'
            if (fillOpacityIndex !== -1 && row[fillOpacityIndex] != null && !styleSettings.noFill) {
                const transparencyValue = String(row[fillOpacityIndex]).trim();
                if (transparencyValue.toLowerCase() === "no fill") {
                     styleSettings.noFill = true; styleSettings.fillOpacity = 0;
                } else if (transparencyValue.endsWith("%")) {
                    const transparencyPercent = parseInt(transparencyValue);
                    if (!isNaN(transparencyPercent)) { styleSettings.fillOpacity = Math.max(0, Math.min(1, (100 - transparencyPercent) / 100)); }
                } else {
                    let opacity = Number(transparencyValue);
                    if (!isNaN(opacity)) {
                        if (opacity > 1 && opacity <= 100) { opacity = (100 - opacity) / 100; } // Assume % transparency if > 1
                        styleSettings.fillOpacity = Math.max(0, Math.min(1, opacity));
                    }
                }
                if (styleSettings.fillOpacity === 0) { styleSettings.noFill = true; } // Sync noFill if opacity becomes 0
            }

             // Extract styles (Border Width) - handle only if not already 'noBorder'
            if (borderWidthIndex !== -1 && row[borderWidthIndex] != null && !styleSettings.noBorder) {
                const borderWidthValue = String(row[borderWidthIndex]).trim();
                 if (borderWidthValue.toLowerCase() === "no border") {
                    styleSettings.noBorder = true; styleSettings.borderWidth = 0;
                } else if (!isNaN(parseFloat(borderWidthValue))) {
                    styleSettings.borderWidth = Math.max(0, Number(borderWidthValue));
                }
                if (styleSettings.borderWidth === 0) { styleSettings.noBorder = true; } // Sync noBorder if width becomes 0
            }

            // Create final market area object
            const marketAreaName = String(row[nameIndex]);
            const marketArea = {
                name: marketAreaName,
                short_name: marketAreaName.substring(0, 20), // Simple truncation for short name
                ma_type: maType,
                description: `Imported from ${fileName}`,
                style_settings: styleSettings,
                project: effectiveProjectId,
                project_id: effectiveProjectId,
                locations: locations // Assign processed locations
            };

            console.log(`Processed row ${i+1} into market area:`, { name: marketArea.name, type: marketArea.ma_type, locCount: marketArea.locations.length, style: marketArea.style_settings });
            marketAreas.push(marketArea);
        }

         console.log(`Finished processing standard data. Found ${marketAreas.length} supported market areas.`);
        return marketAreas;
    };

    // Helper function to ensure color is in hex format with enhanced color name support
    const ensureHexColor = (color) => {
        if (!color)  return "#0078D4"; // Default blue

        // Common color names mapping
        const colorMap = {
            'red': '#FF0000',
            'blue': '#0000FF',
            'green': '#008000',
            'yellow': '#FFFF00',
            'orange': '#FFA500',
            'purple': '#800080',
            'black': '#000000',
            'white': '#FFFFFF',
            'gray': '#808080',
            'grey': '#808080',
            'pink': '#FFC0CB',
            'brown': '#A52A2A',
            'teal': '#008080',
            'navy': '#000080',
            'lime': '#00FF00',
            'cyan': '#00FFFF',
            'magenta': '#FF00FF',
            'maroon': '#800000',
            'olive': '#808000',
            'silver': '#C0C0C0',
            'aqua': '#00FFFF',
            'fuchsia': '#FF00FF'
        };

        // If already a hex color, return as is
        if (typeof color === 'string' && color.startsWith('#')) {
            return color;
        }

        // Check if it's a standard color name
        const lcColor = typeof color === 'string' ? color.toLowerCase() : '';
        if (colorMap[lcColor]) {
            console.log(`Converting color name "${color}" to hex: ${colorMap[lcColor]}`);
            return colorMap[lcColor];
        }

        // Try to convert from string like "rgb(255, 0, 0)" to hex
        if (typeof color === 'string' && color.startsWith('rgb')) {
            try {
                const matches = color.match(/\d+/g);
                if (matches && matches.length >= 3) {
                    const r = parseInt(matches[0]);
                    const g = parseInt(matches[1]);
                    const b = parseInt(matches[2]);
                    const hex = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
                    console.log(`Converting RGB color "${color}" to hex: ${hex}`);
                    return hex;
                }
            } catch (e) {
                console.warn(`Failed to parse RGB color: ${color}`, e);
            }
        }

        // If it looks like a hex color without the #, add it
        if (typeof color === 'string' && /^[0-9A-Fa-f]{6}$/.test(color)) {
            return `#${color}`;
        }

        console.log(`Using default color for unrecognized format: ${color}`);
        return "#0078D4"; // Default blue
    };

    // Helper function to normalize market area type - supports all market area types
    const normalizeMarketAreaType = (type) => {
        if (!type) return null;

        const lcType = type.toLowerCase().trim();

        // Map to supported types
        if (lcType.includes('zip') || lcType.includes('zcta')) return 'zip';
        if (lcType.includes('county')) return 'county';
        if (lcType.includes('place') || lcType.includes('city') || lcType.includes('town')) return 'place';
        if (lcType.includes('tract') || lcType === 'census tract') return 'tract';
        if (lcType.includes('block group') || lcType.includes('blockgroup') || lcType === 'bg') return 'blockgroup';
        if (lcType.includes('block') && !lcType.includes('group')) return 'block';
        if (lcType.includes('cbsa') || lcType.includes('msa') || lcType.includes('metro')) return 'cbsa';
        if (lcType.includes('state')) return 'state';
        if (lcType.includes('radius') || lcType.includes('mile') || lcType.includes('ring')) return 'radius';
        if (lcType.includes('drive') || lcType.includes('drivetime') || lcType.includes('isochrone') || lcType.includes('minute')) return 'drivetime';
        if (lcType.includes('custom') || lcType.includes('polygon') || lcType.includes('shape')) return 'custom';
        if (lcType.includes('site') || lcType.includes('location') || lcType.includes('point')) return 'site_location';
        if (lcType.includes('usa') || lcType.includes('national') || lcType === 'us') return 'usa';

        // Return null for unsupported types
        return null;
    };

    // If not open, don't render anything
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-30" onClick={handleClose}></div>
            <div className="min-h-screen px-4 text-center">
                <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-2xl relative">
                    <div className="flex justify-between items-center">
                        <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                            Import Market Areas
                        </h3>
                        <button
                            onClick={handleClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>

                    <div className="mt-4">
                        <p className="text-sm text-gray-500 dark:text-gray-300 mb-4">
                            Select an Excel file to import market areas. All market area types are supported including ZIP, Place, County, Tract, State, CBSA, Radius, Drive Time, and more.
                        </p>

                        <div className="space-y-4">
                            <div>
                                <label
                                    htmlFor="file-upload"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                                >
                                    Excel File
                                </label>
                                <div className="mt-1 flex items-center">
                                    <input
                                        ref={fileInputRef}
                                        id="file-upload"
                                        name="file-upload"
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleFileChange}
                                        className="block w-full text-sm text-gray-500 dark:text-gray-300
                             file:mr-4 file:py-2 file:px-4
                             file:rounded-md file:border-0
                             file:text-sm file:font-medium
                             file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900 dark:file:text-blue-200
                             hover:file:bg-blue-100 dark:hover:file:bg-blue-800"
                                    />
                                </div>
                                {fileName && (
                                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                        Selected: {fileName}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            type="button"
                            className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-3"
                            onClick={handleClose}
                            disabled={isProcessing}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isProcessing || !fileSelected
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                                }`}
                            onClick={handleProcessFile}
                            disabled={isProcessing || !fileSelected}
                        >
                            {isProcessing ? "Processing..." : "Preview Import"}
                        </button>
                    </div>
                </div>
            </div>

            {/* Preview Modal */}
            {showPreview && (
                <MarketAreaPreviewModal
                    marketAreas={detectedMarketAreas}
                    onClose={() => setShowPreview(false)}
                    onImport={handleImport}
                    isProcessing={isProcessing}
                />
            )}
        </div>
    );
}

// Updated MarketAreaPreviewModal with larger dimensions
function MarketAreaPreviewModal({ marketAreas, onClose, onImport, isProcessing }) {
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [confirmedMarketAreas, setConfirmedMarketAreas] = useState([...marketAreas]);
    const [editedMarketAreas, setEditedMarketAreas] = useState([...marketAreas]);

    // Handle import confirmation
    const handleConfirmImport = () => {
        onImport(confirmedMarketAreas);
    };

    // Update market area data when edited
    const handleMarketAreaUpdate = (index, updatedMarketArea) => {
        // Log what's being updated for debugging
        console.log(`Updating market area ${index}:`, {
            name: updatedMarketArea.name,
            style_settings: updatedMarketArea.style_settings
        });
        
        const newEditedMarketAreas = [...editedMarketAreas];
        newEditedMarketAreas[index] = updatedMarketArea;
        setEditedMarketAreas(newEditedMarketAreas);

        // Update confirmed market areas too
        const newConfirmedMarketAreas = [...confirmedMarketAreas];
        newConfirmedMarketAreas[index] = updatedMarketArea;
        setConfirmedMarketAreas(newConfirmedMarketAreas);
    };

    // Handle tab selection
    const handleTabChange = (index) => {
        setActiveTabIndex(index);
    };

    // Handle market area exclusion toggle
    const handleMarketAreaToggle = (index) => {
        setConfirmedMarketAreas(prev => {
            const updated = [...prev];

            // If it's currently in the array, remove it
            if (updated.includes(editedMarketAreas[index])) {
                return updated.filter(ma => ma !== editedMarketAreas[index]);
            }
            // Otherwise, add it
            else {
                updated[index] = editedMarketAreas[index];
                return updated;
            }
        });
    };

    // Check if market area is included for import
    const isMarketAreaIncluded = (index) => {
        return confirmedMarketAreas.includes(editedMarketAreas[index]);
    };

    return (
        <div className="fixed inset-0 z-20 overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
            <div className="min-h-screen px-4 py-8 flex items-center justify-center">
                {/* Increased max-width from max-w-4xl to max-w-6xl and added min-width */}
                <div className="w-full max-w-6xl min-w-[800px] bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden transition-all transform">
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                        <div className="flex justify-between items-center">
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                                Preview Market Areas
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-500"
                                disabled={isProcessing}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Review and confirm the market areas to be imported.
                        </p>
                    </div>

                    {/* Tabs */}
                    <div className="border-b border-gray-200 dark:border-gray-600">
                        <div className="flex overflow-x-auto">
                            {marketAreas.map((marketArea, index) => (
                                <button
                                    key={index}
                                    className={`px-4 py-2 text-sm font-medium flex items-center ${activeTabIndex === index
                                        ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                        }`}
                                    onClick={() => handleTabChange(index)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isMarketAreaIncluded(index)}
                                        onChange={() => handleMarketAreaToggle(index)}
                                        className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span className="truncate max-w-xs">{marketArea.name}</span>
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                                        {marketArea.ma_type}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Tab content - increased max-h-96 to max-h-[60vh] for more room */}
                    <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                        {marketAreas.length > 0 && (
                            <MarketAreaImportTab
                                marketArea={editedMarketAreas[activeTabIndex]}
                                index={activeTabIndex}
                                onUpdate={handleMarketAreaUpdate}
                            />
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex justify-between items-center">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                            <span className="font-medium">{confirmedMarketAreas.filter(Boolean).length}</span> of <span className="font-medium">{marketAreas.length}</span> market areas selected for import
                        </div>
                        <div className="flex space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                disabled={isProcessing}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmImport}
                                className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isProcessing || confirmedMarketAreas.filter(Boolean).length === 0
                                    ? "opacity-50 cursor-not-allowed"
                                    : ""
                                    }`}
                                disabled={isProcessing || confirmedMarketAreas.filter(Boolean).length === 0}
                            >
                                {isProcessing ? "Importing..." : "Import Selected"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function MarketAreaImportTab({ marketArea, index, onUpdate }) {
    // Create editable copy of market area with ref tracking for theme sync
    const [editableMarketArea, setEditableMarketArea] = useState({ ...marketArea });
    const [forceKey, setForceKey] = useState(0); // Force re-render key

    // Only update internal state when marketArea prop changes
    useEffect(() => {
        setEditableMarketArea(marketArea);
    }, [marketArea]);

    // Handle input changes
    const handleInputChange = (field, value) => {
        const updatedMarketArea = {
            ...editableMarketArea,
            [field]: value
        };
        setEditableMarketArea(updatedMarketArea);
        onUpdate(index, updatedMarketArea);
    };
    // Handle style changes with debugging and force refresh
    const handleStyleChange = (type, value) => {
        console.log(`Style change: ${type} = ${value}`);
        
        // Handle batch updates from theme selection
        if (type === "batchUpdate" || type === "completeThemeUpdate") {
            // Create a completely new market area object with spread to avoid reference issues
            const updatedMarketArea = JSON.parse(JSON.stringify({
                ...editableMarketArea,
                style_settings: {
                    ...(editableMarketArea.style_settings || {}),
                    ...(type === "completeThemeUpdate" ? value.styles : value),
                    // Ensure theme name is captured
                    themeName: type === "completeThemeUpdate" ? value.theme : (value.themeName || currentThemeName)
                }
            }));
            
            console.log("Batch updating market area with theme:", updatedMarketArea);
            
            // Force component refresh with a larger key increment for batch updates
            setForceKey(prevKey => prevKey + 100);
            
            // First update local state to ensure UI consistency
            setEditableMarketArea(updatedMarketArea);
            
            // Then notify parent component
            setTimeout(() => {
                onUpdate(index, updatedMarketArea);
            }, 0);
            
            return;
        }
        
        // For individual property updates
        const updatedMarketArea = JSON.parse(JSON.stringify(editableMarketArea)); // Deep clone
        const newStyleSettings = { ...(updatedMarketArea.style_settings || {}) };

        if (type === "noBorder") {
            newStyleSettings.noBorder = value;
            if (value) {
                newStyleSettings.borderWidth = 0;
            } else if (newStyleSettings.borderWidth === 0) {
                newStyleSettings.borderWidth = 3;
            }
        } else if (type === "noFill") {
            newStyleSettings.noFill = value;
            if (value) {
                newStyleSettings.fillOpacity = 0;
            } else if (newStyleSettings.fillOpacity === 0) {
                newStyleSettings.fillOpacity = 0.35;
            }
        } else if (type === "fillOpacity") {
            newStyleSettings.fillOpacity = Math.max(0, Math.min(1, value));
            if (newStyleSettings.fillOpacity === 0) {
                newStyleSettings.noFill = true;
            } else {
                newStyleSettings.noFill = false;
            }
        } else if (type === "borderWidth") {
            newStyleSettings.borderWidth = Math.max(0, Number(value));
            if (newStyleSettings.borderWidth === 0) {
                newStyleSettings.noBorder = true;
            } else {
                newStyleSettings.noBorder = false;
            }
        } else {
            newStyleSettings[type] = value;
        }

        updatedMarketArea.style_settings = newStyleSettings;
        
        console.log("Updated market area after style change:", updatedMarketArea);
        
        // Force component refresh with new key
        setForceKey(prevKey => prevKey + 1);
        
        // Update local state first
        setEditableMarketArea(updatedMarketArea);
        
        // Then notify parent component
        onUpdate(index, updatedMarketArea);
    };

    // In StyleSettingsPanel.jsx
    const handleThemeSelect = (newSettings) => {
        console.log('StyleSettingsPanel received new settings:', newSettings);
        
        // Force a complete UI refresh with a specific key update
        setForceKey(prevKey => prevKey + 100); // Use a larger increment to ensure a fresh key
        
        // Instead of updating each property individually, update the entire style object
        // in the parent component at once
        
        // Update the current theme name immediately
        setCurrentThemeName(newSettings.themeName || 'Custom');
        
        // Create a complete theme update to pass to parent
        const fullStyleUpdate = {
        type: 'COMPLETE_THEME_UPDATE',
        theme: newSettings.themeName || 'Custom',
        styles: {
            fillColor: newSettings.fillColor,
            fillOpacity: newSettings.fillOpacity,
            borderColor: newSettings.borderColor,
            borderWidth: newSettings.borderWidth,
            excelFill: newSettings.excelFill || '#ffffff',
            excelText: newSettings.excelText || '#000000',
            noFill: newSettings.noFill,
            noBorder: newSettings.noBorder
        }
        };
        
        // Close theme selector
        setIsThemeSelectorOpen(false);
        
        // Notify parent with full theme update
        if (typeof onStyleChange === 'function') {
        onStyleChange('completeThemeUpdate', fullStyleUpdate);
        }
    };

    // Function to determine what to display for locations
    const renderLocationsSummary = () => {
        const locations = editableMarketArea.locations || [];

        if (locations.length === 0) {
            return <p className="text-gray-500 dark:text-gray-400 italic">No locations defined</p>;
        }

        if (locations.length <= 5) {
            return (
                <ul className="list-disc list-inside text-sm">
                    {locations.map((loc, i) => (
                        <li key={i} className="text-gray-700 dark:text-gray-300">
                            {loc.name || loc.id}
                            {loc.state ? ` (${loc.state})` : ''}
                        </li>
                    ))}
                </ul>
            );
        }

        return (
            <div>
                <p className="text-gray-700 dark:text-gray-300 mb-2">
                    {locations.length} locations defined
                </p>
                <ul className="list-disc list-inside text-sm">
                    {locations.slice(0, 3).map((loc, i) => (
                        <li key={i} className="text-gray-700 dark:text-gray-300">
                            {loc.name || loc.id}
                            {loc.state ? ` (${loc.state})` : ''}
                        </li>
                    ))}
                    <li className="text-gray-500 italic">plus {locations.length - 3} more...</li>
                </ul>
            </div>
        );
    };

    // Ensure the market area has a style_settings object
    const styleSettings = editableMarketArea.style_settings || {
        fillColor: "#0078D4",
        fillOpacity: 0.35,
        borderColor: "#0078D4",
        borderWidth: 3,
        noFill: false,
        noBorder: false
    };

    return (
        <div className="space-y-6">
            {/* Basic Information */}
            <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                    Basic Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Market Area Name
                        </label>
                        <input
                            type="text"
                            value={editableMarketArea.name || ''}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:text-white text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Short Name
                        </label>
                        <input
                            type="text"
                            value={editableMarketArea.short_name || ''}
                            onChange={(e) => handleInputChange('short_name', e.target.value)}
                            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-3 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:text-white text-sm"
                        />
                    </div>
                </div>
                <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Market Area Type
                    </label>
                    <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-md py-2 px-3 text-sm text-gray-700 dark:text-gray-300">
                        {editableMarketArea.ma_type || 'Unknown'}
                    </div>
                </div>
            </div>

            {/* Style Settings - adding key to force re-render */}
            <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                    Style Settings
                </h4>
                <StyleSettingsPanel
                    key={`style-panel-${forceKey}`}
                    styleSettings={styleSettings}
                    onStyleChange={handleStyleChange}
                    currentTheme={styleSettings.themeName || 'Default'}
                />
            </div>

            {/* Locations */}
            <div>
                <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                    Locations
                </h4>
                {renderLocationsSummary()}
            </div>
        </div>
    );
}